/**
 * Onboarding Media Configuration
 *
 * Defines video, GIF, and audio resources for each onboarding step.
 * Media can be hosted externally or as local assets.
 */

// Base URL for media assets (can be CDN or local)
const MEDIA_BASE_URL = '/assets/onboarding';

/**
 * Step media configuration
 * Each step can have:
 * - video: URL to video file (mp4, webm)
 * - gif: URL to animated GIF
 * - thumbnail: Static image fallback
 * - audio: URL to audio narration file
 * - audioDuration: Duration in seconds (for progress)
 * - estimatedTime: Time to complete step in seconds
 */
export const STEP_MEDIA = {
  welcome: {
    gif: `${MEDIA_BASE_URL}/welcome-animation.gif`,
    thumbnail: `${MEDIA_BASE_URL}/welcome-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/welcome.mp3`,
      es: `${MEDIA_BASE_URL}/audio/es/welcome.mp3`,
    },
    audioDuration: 15,
    estimatedTime: 20,
  },
  dashboard: {
    video: `${MEDIA_BASE_URL}/dashboard-tour.mp4`,
    gif: `${MEDIA_BASE_URL}/dashboard-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/dashboard-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/dashboard.mp3`,
      es: `${MEDIA_BASE_URL}/audio/es/dashboard.mp3`,
    },
    audioDuration: 20,
    estimatedTime: 30,
  },
  search: {
    video: `${MEDIA_BASE_URL}/search-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/search-demo.gif`,
    thumbnail: `${MEDIA_BASE_URL}/search-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/search.mp3`,
      es: `${MEDIA_BASE_URL}/audio/es/search.mp3`,
    },
    audioDuration: 12,
    estimatedTime: 15,
  },
  'quick-actions': {
    gif: `${MEDIA_BASE_URL}/quick-actions.gif`,
    thumbnail: `${MEDIA_BASE_URL}/quick-actions-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/quick-actions.mp3`,
      es: `${MEDIA_BASE_URL}/audio/es/quick-actions.mp3`,
    },
    audioDuration: 10,
    estimatedTime: 15,
  },
  'budget-transfers': {
    video: `${MEDIA_BASE_URL}/transfers-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/transfers-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/transfers-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/transfers.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'budget-scenarios': {
    video: `${MEDIA_BASE_URL}/scenarios-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/scenarios-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/scenarios-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/scenarios.mp3`,
    },
    audioDuration: 20,
    estimatedTime: 30,
  },
  'research-portfolio': {
    gif: `${MEDIA_BASE_URL}/research-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research.mp3`,
    },
    audioDuration: 15,
    estimatedTime: 20,
  },
  positions: {
    video: `${MEDIA_BASE_URL}/positions-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/positions-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/positions-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/positions.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'personnel-whatif': {
    video: `${MEDIA_BASE_URL}/personnel-whatif-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/personnel-whatif.gif`,
    thumbnail: `${MEDIA_BASE_URL}/personnel-whatif-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/personnel-whatif.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'research-commitments': {
    gif: `${MEDIA_BASE_URL}/research-commitments.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-commitments-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-commitments.mp3`,
    },
    audioDuration: 16,
    estimatedTime: 22,
  },
  'revenue-recognition': {
    video: `${MEDIA_BASE_URL}/revenue-recognition-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/revenue-recognition.gif`,
    thumbnail: `${MEDIA_BASE_URL}/revenue-recognition-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/revenue-recognition.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'program-margins': {
    video: `${MEDIA_BASE_URL}/program-margins-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/program-margins.gif`,
    thumbnail: `${MEDIA_BASE_URL}/program-margins-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/program-margins.mp3`,
    },
    audioDuration: 16,
    estimatedTime: 22,
  },

  // Forms & Dynamic Submissions
  'form-builder': {
    video: `${MEDIA_BASE_URL}/form-builder-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/form-builder.gif`,
    thumbnail: `${MEDIA_BASE_URL}/form-builder-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/form-builder.mp3`,
    },
    audioDuration: 22,
    estimatedTime: 30,
  },
  'form-submissions': {
    gif: `${MEDIA_BASE_URL}/form-submissions.gif`,
    thumbnail: `${MEDIA_BASE_URL}/form-submissions-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/form-submissions.mp3`,
    },
    audioDuration: 14,
    estimatedTime: 18,
  },
  'public-forms': {
    gif: `${MEDIA_BASE_URL}/public-forms.gif`,
    thumbnail: `${MEDIA_BASE_URL}/public-forms-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/public-forms.mp3`,
    },
    audioDuration: 12,
    estimatedTime: 16,
  },

  admin: {
    video: `${MEDIA_BASE_URL}/admin-tour.mp4`,
    gif: `${MEDIA_BASE_URL}/admin-preview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/admin-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/admin.mp3`,
    },
    audioDuration: 22,
    estimatedTime: 30,
  },
  complete: {
    gif: `${MEDIA_BASE_URL}/celebration.gif`,
    thumbnail: `${MEDIA_BASE_URL}/complete-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/complete.mp3`,
      es: `${MEDIA_BASE_URL}/audio/es/complete.mp3`,
    },
    audioDuration: 10,
    estimatedTime: 10,
  },

  // Campus & Department Management
  'campus-summary': {
    gif: `${MEDIA_BASE_URL}/campus-summary.gif`,
    thumbnail: `${MEDIA_BASE_URL}/campus-summary-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/campus-summary.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'department-overview': {
    gif: `${MEDIA_BASE_URL}/department-overview.gif`,
    thumbnail: `${MEDIA_BASE_URL}/department-overview-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/department-overview.mp3`,
    },
    audioDuration: 15,
    estimatedTime: 20,
  },
  'budget-model': {
    video: `${MEDIA_BASE_URL}/budget-model-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/budget-model.gif`,
    thumbnail: `${MEDIA_BASE_URL}/budget-model-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/budget-model.mp3`,
    },
    audioDuration: 22,
    estimatedTime: 30,
  },
  'budget-vs-actual': {
    video: `${MEDIA_BASE_URL}/budget-vs-actual-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/budget-vs-actual.gif`,
    thumbnail: `${MEDIA_BASE_URL}/budget-vs-actual-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/budget-vs-actual.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },

  // Capital Planning
  'capital-projects': {
    video: `${MEDIA_BASE_URL}/capital-projects-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/capital-projects.gif`,
    thumbnail: `${MEDIA_BASE_URL}/capital-projects-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/capital-projects.mp3`,
    },
    audioDuration: 20,
    estimatedTime: 28,
  },
  'capital-planning': {
    video: `${MEDIA_BASE_URL}/capital-planning-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/capital-planning.gif`,
    thumbnail: `${MEDIA_BASE_URL}/capital-planning-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/capital-planning.mp3`,
    },
    audioDuration: 22,
    estimatedTime: 30,
  },
  'capital-portfolio': {
    gif: `${MEDIA_BASE_URL}/capital-portfolio.gif`,
    thumbnail: `${MEDIA_BASE_URL}/capital-portfolio-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/capital-portfolio.mp3`,
    },
    audioDuration: 16,
    estimatedTime: 22,
  },

  // Tuition & Enrollment
  'fee-projection': {
    video: `${MEDIA_BASE_URL}/fee-projection-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/fee-projection.gif`,
    thumbnail: `${MEDIA_BASE_URL}/fee-projection-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/fee-projection.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'enrollment-funnel': {
    gif: `${MEDIA_BASE_URL}/enrollment-funnel.gif`,
    thumbnail: `${MEDIA_BASE_URL}/enrollment-funnel-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/enrollment-funnel.mp3`,
    },
    audioDuration: 15,
    estimatedTime: 20,
  },

  // Expanded Research Module
  'research-explorer': {
    video: `${MEDIA_BASE_URL}/research-explorer-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/research-explorer.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-explorer-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-explorer.mp3`,
    },
    audioDuration: 18,
    estimatedTime: 25,
  },
  'research-proposals': {
    gif: `${MEDIA_BASE_URL}/research-proposals.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-proposals-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-proposals.mp3`,
    },
    audioDuration: 16,
    estimatedTime: 22,
  },
  'research-subawards': {
    gif: `${MEDIA_BASE_URL}/research-subawards.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-subawards-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-subawards.mp3`,
    },
    audioDuration: 14,
    estimatedTime: 18,
  },
  'research-budget-builder': {
    video: `${MEDIA_BASE_URL}/research-budget-builder-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/research-budget-builder.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-budget-builder-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-budget-builder.mp3`,
    },
    audioDuration: 20,
    estimatedTime: 28,
  },
  'research-closeout': {
    gif: `${MEDIA_BASE_URL}/research-closeout.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-closeout-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-closeout.mp3`,
    },
    audioDuration: 12,
    estimatedTime: 18,
  },
  'research-compliance': {
    gif: `${MEDIA_BASE_URL}/research-compliance.gif`,
    thumbnail: `${MEDIA_BASE_URL}/research-compliance-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/research-compliance.mp3`,
    },
    audioDuration: 14,
    estimatedTime: 20,
  },

  // Analytics & Reporting
  'analytics-bi': {
    video: `${MEDIA_BASE_URL}/analytics-bi-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/analytics-bi.gif`,
    thumbnail: `${MEDIA_BASE_URL}/analytics-bi-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/analytics-bi.mp3`,
    },
    audioDuration: 20,
    estimatedTime: 28,
  },
  'budget-book': {
    gif: `${MEDIA_BASE_URL}/budget-book.gif`,
    thumbnail: `${MEDIA_BASE_URL}/budget-book-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/budget-book.mp3`,
    },
    audioDuration: 14,
    estimatedTime: 18,
  },
  'deficit-centers': {
    gif: `${MEDIA_BASE_URL}/deficit-centers.gif`,
    thumbnail: `${MEDIA_BASE_URL}/deficit-centers-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/deficit-centers.mp3`,
    },
    audioDuration: 12,
    estimatedTime: 16,
  },

  // Workflow & Approvals
  'approval-queue': {
    video: `${MEDIA_BASE_URL}/approval-queue-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/approval-queue.gif`,
    thumbnail: `${MEDIA_BASE_URL}/approval-queue-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/approval-queue.mp3`,
    },
    audioDuration: 16,
    estimatedTime: 22,
  },
  'workflow-builder': {
    video: `${MEDIA_BASE_URL}/workflow-builder-demo.mp4`,
    gif: `${MEDIA_BASE_URL}/workflow-builder.gif`,
    thumbnail: `${MEDIA_BASE_URL}/workflow-builder-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/workflow-builder.mp3`,
    },
    audioDuration: 22,
    estimatedTime: 30,
  },

  // Help & Support
  'help-center': {
    gif: `${MEDIA_BASE_URL}/help-center.gif`,
    thumbnail: `${MEDIA_BASE_URL}/help-center-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/help-center.mp3`,
    },
    audioDuration: 10,
    estimatedTime: 15,
  },
  'account-settings': {
    gif: `${MEDIA_BASE_URL}/account-settings.gif`,
    thumbnail: `${MEDIA_BASE_URL}/account-settings-thumb.png`,
    audio: {
      en: `${MEDIA_BASE_URL}/audio/en/account-settings.mp3`,
    },
    audioDuration: 12,
    estimatedTime: 18,
  },
};

/**
 * Achievement definitions
 */
export const ACHIEVEMENTS = {
  // Onboarding achievements
  first_steps: {
    id: 'first_steps',
    title: 'First Steps',
    description: 'Started the onboarding tour',
    icon: 'Footprints',
    points: 10,
    category: 'onboarding',
  },
  quick_learner: {
    id: 'quick_learner',
    title: 'Quick Learner',
    description: 'Completed onboarding in under 3 minutes',
    icon: 'Zap',
    points: 25,
    category: 'onboarding',
  },
  thorough_explorer: {
    id: 'thorough_explorer',
    title: 'Thorough Explorer',
    description: 'Viewed all onboarding steps without skipping',
    icon: 'Compass',
    points: 30,
    category: 'onboarding',
  },
  tour_complete: {
    id: 'tour_complete',
    title: 'Tour Complete',
    description: 'Finished the onboarding wizard',
    icon: 'Trophy',
    points: 50,
    category: 'onboarding',
  },

  // Checklist achievements
  checklist_starter: {
    id: 'checklist_starter',
    title: 'Getting Started',
    description: 'Completed your first checklist item',
    icon: 'CheckSquare',
    points: 15,
    category: 'checklist',
  },
  checklist_half: {
    id: 'checklist_half',
    title: 'Halfway There',
    description: 'Completed half of the getting started checklist',
    icon: 'Target',
    points: 25,
    category: 'checklist',
  },
  checklist_complete: {
    id: 'checklist_complete',
    title: 'Ready to Roll',
    description: 'Completed all getting started tasks',
    icon: 'Award',
    points: 75,
    category: 'checklist',
  },

  // Feature discovery achievements
  search_master: {
    id: 'search_master',
    title: 'Search Master',
    description: 'Used global search 10 times',
    icon: 'Search',
    points: 20,
    category: 'discovery',
  },
  keyboard_ninja: {
    id: 'keyboard_ninja',
    title: 'Keyboard Ninja',
    description: 'Used keyboard shortcuts 25 times',
    icon: 'Keyboard',
    points: 30,
    category: 'discovery',
  },
  dark_side: {
    id: 'dark_side',
    title: 'Dark Side',
    description: 'Enabled dark mode',
    icon: 'Moon',
    points: 10,
    category: 'discovery',
  },
  filter_pro: {
    id: 'filter_pro',
    title: 'Filter Pro',
    description: 'Saved your first filter',
    icon: 'Filter',
    points: 15,
    category: 'discovery',
  },

  // Power user achievements
  power_user: {
    id: 'power_user',
    title: 'Power User',
    description: 'Earned 200 total achievement points',
    icon: 'Star',
    points: 100,
    category: 'milestone',
    requirement: { totalPoints: 200 },
  },
  uni_fund_expert: {
    id: 'uni_fund_expert',
    title: 'Uni-Fund Expert',
    description: 'Unlocked all achievements',
    icon: 'Crown',
    points: 250,
    category: 'milestone',
    requirement: { allAchievements: true },
  },
};

/**
 * User goal definitions for branching paths
 */
export const USER_GOALS = {
  manage_budgets: {
    id: 'manage_budgets',
    title: 'Manage Budgets',
    description: 'I want to track and manage department budgets',
    icon: 'Wallet',
    steps: ['welcome', 'dashboard', 'search', 'department-overview', 'budget-transfers', 'budget-scenarios', 'budget-vs-actual', 'complete'],
    roles: ['budget_owner', 'dept_manager', 'finance', 'admin'],
  },
  review_approvals: {
    id: 'review_approvals',
    title: 'Review Approvals',
    description: 'I need to review and approve requests',
    icon: 'CheckCircle',
    steps: ['welcome', 'dashboard', 'search', 'quick-actions', 'approval-queue', 'complete'],
    roles: ['approver', 'admin', 'dept_manager'],
  },
  track_research: {
    id: 'track_research',
    title: 'Track Research Funding',
    description: 'I manage grants and research commitments',
    icon: 'FlaskConical',
    steps: ['welcome', 'dashboard', 'search', 'research-portfolio', 'research-explorer', 'research-proposals', 'research-budget-builder', 'research-commitments', 'research-closeout', 'complete'],
    roles: ['researcher', 'pi', 'research_admin'],
  },
  manage_positions: {
    id: 'manage_positions',
    title: 'Manage Positions',
    description: 'I handle position budgeting and personnel planning',
    icon: 'Users',
    steps: ['welcome', 'dashboard', 'positions', 'personnel-whatif', 'complete'],
    roles: ['hr', 'dept_manager', 'admin'],
  },
  capital_planning: {
    id: 'capital_planning',
    title: 'Capital Planning',
    description: 'I manage capital projects and facilities budgets',
    icon: 'Building',
    steps: ['welcome', 'dashboard', 'search', 'capital-projects', 'capital-planning', 'capital-portfolio', 'complete'],
    roles: ['capital_planner', 'facilities', 'finance', 'admin'],
  },
  tuition_strategy: {
    id: 'tuition_strategy',
    title: 'Tuition & Enrollment',
    description: 'I work on tuition modeling and enrollment forecasting',
    icon: 'GraduationCap',
    steps: ['welcome', 'dashboard', 'search', 'fee-projection', 'enrollment-funnel', 'complete'],
    roles: ['enrollment_mgr', 'tuition_officer', 'finance', 'admin'],
  },
  analytics_insights: {
    id: 'analytics_insights',
    title: 'Analytics & Reporting',
    description: 'I create reports and analyze financial data',
    icon: 'BarChart3',
    steps: ['welcome', 'dashboard', 'search', 'analytics-bi', 'budget-book', 'budget-vs-actual', 'deficit-centers', 'complete'],
    roles: ['analyst', 'finance', 'admin', 'dept_manager'],
  },
  campus_overview: {
    id: 'campus_overview',
    title: 'Campus Overview',
    description: 'I need to see the big picture across departments',
    icon: 'Building2',
    steps: ['welcome', 'dashboard', 'search', 'campus-summary', 'department-overview', 'budget-model', 'complete'],
    roles: ['provost', 'dean', 'vp_finance', 'admin'],
  },
  workflow_management: {
    id: 'workflow_management',
    title: 'Workflow Management',
    description: 'I configure and manage approval workflows',
    icon: 'GitBranch',
    steps: ['welcome', 'dashboard', 'search', 'workflow-builder', 'approval-queue', 'complete'],
    roles: ['workflow_admin', 'admin', 'superadmin'],
  },
  explore_everything: {
    id: 'explore_everything',
    title: 'Explore Everything',
    description: 'Show me the full tour of all features',
    icon: 'Compass',
    steps: null, // null means show all available steps
    roles: [],
  },
  admin_setup: {
    id: 'admin_setup',
    title: 'Configure System',
    description: "I'm setting up Uni-Fund for my organization",
    icon: 'Settings',
    steps: ['welcome', 'dashboard', 'admin', 'workflow-builder', 'account-settings', 'help-center', 'complete'],
    roles: ['admin', 'superadmin'],
  },
  form_administration: {
    id: 'form_administration',
    title: 'Form Administration',
    description: 'I create and manage dynamic forms and submissions',
    icon: 'FileText',
    steps: ['welcome', 'dashboard', 'search', 'form-builder', 'form-submissions', 'public-forms', 'workflow-builder', 'complete'],
    roles: ['form_admin', 'admin', 'superadmin'],
  },
};

/**
 * Help center article mappings
 * URLs use the query param format to link directly to specific topics
 */
export const HELP_ARTICLES = {
  welcome: {
    articleId: 'getting-started',
    title: 'Getting Started Guide',
    url: '/help?topic=getting-started',
  },
  dashboard: {
    articleId: 'dashboard',
    title: 'Dashboard Overview',
    url: '/help?topic=dashboard',
  },
  search: {
    articleId: 'cmdk-search',
    title: 'Using Global Search',
    url: '/help?topic=cmdk-search',
    videoUrl: '/help/videos',
  },
  'quick-actions': {
    articleId: 'quick-actions',
    title: 'Quick Actions Guide',
    url: '/help?topic=quick-actions',
  },
  'budget-transfers': {
    articleId: 'transfers-create',
    title: 'Budget Transfers',
    url: '/help?topic=transfers-create',
    videoUrl: '/help/videos',
  },
  'budget-scenarios': {
    articleId: 'budget-scenarios',
    title: 'Working with Scenarios',
    url: '/help?topic=budget-scenarios',
  },
  'research-portfolio': {
    articleId: 'research-overview',
    title: 'Research Portfolio Management',
    url: '/help?topic=research-overview',
  },
  'research-commitments': {
    articleId: 'research-commitments',
    title: 'Research Commitments',
    url: '/help?topic=research-commitments',
  },
  positions: {
    articleId: 'positions',
    title: 'Position Budgeting',
    url: '/help?topic=positions',
  },
  'personnel-whatif': {
    articleId: 'personnel-whatif',
    title: 'Personnel What-If Analysis',
    url: '/help?topic=personnel-whatif',
  },
  'revenue-recognition': {
    articleId: 'revenue-recognition',
    title: 'Revenue Recognition',
    url: '/help?topic=revenue-recognition',
  },
  'program-margins': {
    articleId: 'program-margins',
    title: 'Program Margin Analysis',
    url: '/help?topic=program-margins',
  },
  admin: {
    articleId: 'admin-guide',
    title: 'Administrator Guide',
    url: '/help?topic=admin-guide',
  },
  // Campus & Department Management
  'campus-summary': {
    articleId: 'campus-summary',
    title: 'Campus Summary Dashboard',
    url: '/help?topic=campus-summary',
  },
  'department-overview': {
    articleId: 'department-overview',
    title: 'Department Overview',
    url: '/help?topic=department-overview',
  },
  'budget-model': {
    articleId: 'budget-model',
    title: 'Budget Model Configuration',
    url: '/help?topic=budget-model',
  },
  'budget-vs-actual': {
    articleId: 'budget-vs-actual',
    title: 'Budget vs Actual Reports',
    url: '/help?topic=budget-vs-actual',
  },
  // Capital Planning
  'capital-projects': {
    articleId: 'capital-projects',
    title: 'Capital Projects Management',
    url: '/help?topic=capital-projects',
    videoUrl: '/help/videos',
  },
  'capital-planning': {
    articleId: 'capital-planning',
    title: 'Capital Planning Guide',
    url: '/help?topic=capital-planning',
  },
  'capital-portfolio': {
    articleId: 'capital-portfolio',
    title: 'Capital Portfolio Dashboard',
    url: '/help?topic=capital-portfolio',
  },
  // Tuition & Enrollment
  'fee-projection': {
    articleId: 'fee-projection',
    title: 'Fee Projection Tool',
    url: '/help?topic=fee-projection',
  },
  'enrollment-funnel': {
    articleId: 'enrollment-funnel',
    title: 'Enrollment Funnel Analysis',
    url: '/help?topic=enrollment-funnel',
  },
  // Extended Research
  'research-explorer': {
    articleId: 'research-explorer',
    title: 'Research Explorer',
    url: '/help?topic=research-explorer',
  },
  'research-proposals': {
    articleId: 'research-proposals',
    title: 'Research Proposals',
    url: '/help?topic=research-proposals',
  },
  'research-subawards': {
    articleId: 'research-subawards',
    title: 'Subaward Management',
    url: '/help?topic=research-subawards',
  },
  'research-budget-builder': {
    articleId: 'research-budget-builder',
    title: 'Research Budget Builder',
    url: '/help?topic=research-budget-builder',
    videoUrl: '/help/videos',
  },
  'research-closeout': {
    articleId: 'research-closeout',
    title: 'Grant Closeout Readiness',
    url: '/help?topic=research-closeout',
  },
  'research-compliance': {
    articleId: 'research-compliance',
    title: 'Research Compliance & Risk',
    url: '/help?topic=research-compliance',
  },
  // Analytics & Reporting
  'analytics-bi': {
    articleId: 'analytics-bi',
    title: 'Analytics & BI Dashboards',
    url: '/help?topic=analytics-bi',
    videoUrl: '/help/videos',
  },
  'budget-book': {
    articleId: 'budget-book',
    title: 'Budget Book Report',
    url: '/help?topic=budget-book',
  },
  'deficit-centers': {
    articleId: 'deficit-centers',
    title: 'Deficit Cost Centers',
    url: '/help?topic=deficit-centers',
  },
  // Workflow & Approvals
  'approval-queue': {
    articleId: 'approval-queue',
    title: 'Approval Queue',
    url: '/help?topic=approval-queue',
  },
  'workflow-builder': {
    articleId: 'workflow-builder',
    title: 'Workflow Builder',
    url: '/help?topic=workflow-builder',
    videoUrl: '/help/videos',
  },
  // Help & Settings
  'help-center': {
    articleId: 'help-center',
    title: 'Help Center Guide',
    url: '/help?topic=help-center',
  },
  'account-settings': {
    articleId: 'account-settings',
    title: 'Account Settings',
    url: '/help?topic=account-settings',
  },
  // Forms & Dynamic Submissions
  'form-builder': {
    articleId: 'form-builder',
    title: 'Form Builder Guide',
    url: '/help?topic=form-builder',
    videoUrl: '/help/videos',
  },
  'form-submissions': {
    articleId: 'form-submissions',
    title: 'Managing Form Submissions',
    url: '/help?topic=form-submissions',
  },
  'public-forms': {
    articleId: 'public-forms',
    title: 'Public Forms Setup',
    url: '/help?topic=public-forms',
  },
};

/**
 * Get total estimated time for a set of steps
 * @param {string[]} stepIds - Array of step IDs
 * @returns {number} Total time in seconds
 */
export function getEstimatedTime(stepIds) {
  return stepIds.reduce((total, id) => {
    const media = STEP_MEDIA[id];
    return total + (media?.estimatedTime || 20);
  }, 0);
}

/**
 * Format time in seconds to human-readable string
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time (e.g., "2 min", "30 sec")
 */
export function formatTime(seconds) {
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}

/**
 * How-To Steps - Detailed step-by-step instructions for in-depth walkthrough mode
 *
 * Each step can have:
 * - instructions: Array of step-by-step instructions
 * - tips: Array of pro tips
 * - warnings: Array of important warnings
 * - tryItAction: Interactive action the user can try
 * - prerequisites: What user should know/have before this step
 * - nextSteps: What to do after completing this step
 */
export const HOW_TO_STEPS = {
  // Core Navigation
  dashboard: {
    instructions: [
      { step: 1, text: 'Your dashboard shows a summary of tasks requiring your attention', highlight: 'tasks-widget' },
      { step: 2, text: 'Click on any task card to navigate directly to that item', highlight: 'task-card' },
      { step: 3, text: 'Use the date range filter to view historical or upcoming items', highlight: 'date-filter' },
      { step: 4, text: 'The metrics section shows real-time budget status across your departments', highlight: 'metrics-section' },
      { step: 5, text: 'Click "View All" on any widget to see the full list', highlight: 'view-all-btn' },
    ],
    tips: [
      'Pin your most-used widgets by clicking the pin icon',
      'Drag widgets to rearrange your dashboard layout',
      'Use keyboard shortcut "D" to quickly return to dashboard from anywhere',
    ],
    tryItAction: {
      label: 'Explore Dashboard',
      path: '/dashboard',
      description: 'Click to open your personalized dashboard',
    },
  },

  search: {
    instructions: [
      { step: 1, text: 'Press Cmd+K (Mac) or Ctrl+K (Windows) to open the command palette', highlight: 'search-trigger' },
      { step: 2, text: 'Start typing to search for pages, actions, or data', highlight: 'search-input' },
      { step: 3, text: 'Use arrow keys to navigate results and Enter to select', highlight: 'search-results' },
      { step: 4, text: 'Type ">" to see available quick actions', highlight: 'quick-commands' },
      { step: 5, text: 'Recent searches appear automatically for quick access', highlight: 'recent-searches' },
    ],
    tips: [
      'Type "/" followed by a page name for instant navigation (e.g., "/transfers")',
      'Search supports fuzzy matching - "bgt trf" finds "Budget Transfers"',
      'Press Escape twice to close the search palette',
    ],
    tryItAction: {
      label: 'Try Global Search',
      action: 'openSearch',
      description: 'Press Cmd+K to open the command palette now',
    },
  },

  'quick-actions': {
    instructions: [
      { step: 1, text: 'Find the floating action button (FAB) in the bottom-right corner', highlight: 'fab-button' },
      { step: 2, text: 'Click or hover to reveal quick action options', highlight: 'fab-menu' },
      { step: 3, text: 'Actions shown are based on your role and current page context', highlight: 'action-list' },
      { step: 4, text: 'Click any action to start that workflow immediately', highlight: 'action-item' },
    ],
    tips: [
      'The FAB adapts to show relevant actions based on your current page',
      'Admin users see additional configuration options',
      'Press "Q" as a keyboard shortcut to toggle the quick actions menu',
    ],
    warnings: [
      'Some actions may require specific permissions - contact your admin if needed',
    ],
  },

  // Budget Management
  'budget-transfers': {
    instructions: [
      { step: 1, text: 'Navigate to Budget → Transfers from the main menu', highlight: 'nav-transfers' },
      { step: 2, text: 'Click "New Transfer" to create a budget transfer request', highlight: 'new-transfer-btn' },
      { step: 3, text: 'Select the source cost center (where funds come from)', highlight: 'source-select' },
      { step: 4, text: 'Select the destination cost center (where funds go to)', highlight: 'dest-select' },
      { step: 5, text: 'Enter the transfer amount and add a justification', highlight: 'amount-input' },
      { step: 6, text: 'Attach supporting documents if required', highlight: 'attachments' },
      { step: 7, text: 'Click "Submit for Approval" to start the workflow', highlight: 'submit-btn' },
    ],
    tips: [
      'Use the "Bulk Transfer" option for multiple transfers at once',
      'Save as draft if you need to gather more information',
      'Check the "History" tab to see previous transfers for reference',
    ],
    warnings: [
      'Transfers may require multiple levels of approval based on amount',
      'Some account combinations may be restricted by policy',
    ],
    prerequisites: [
      'Know your source and destination cost center codes',
      'Have approval authority or know your approval chain',
    ],
    tryItAction: {
      label: 'Create a Transfer',
      path: '/budget/transfers/new',
      description: 'Start a new budget transfer request',
    },
  },

  'budget-scenarios': {
    instructions: [
      { step: 1, text: 'Go to Budget → Scenarios to access the scenario planner', highlight: 'nav-scenarios' },
      { step: 2, text: 'Click "Create Scenario" to start a new what-if analysis', highlight: 'create-scenario-btn' },
      { step: 3, text: 'Give your scenario a descriptive name (e.g., "FY26 5% Reduction")', highlight: 'scenario-name' },
      { step: 4, text: 'Choose a base scenario to start from (usually "Current Budget")', highlight: 'base-scenario' },
      { step: 5, text: 'Apply adjustments using percentage, flat amount, or formula', highlight: 'adjustment-type' },
      { step: 6, text: 'Review the impact summary showing changes by category', highlight: 'impact-summary' },
      { step: 7, text: 'Save and optionally share with colleagues for review', highlight: 'save-share-btn' },
    ],
    tips: [
      'Use the comparison view to see multiple scenarios side-by-side',
      'Export scenarios to Excel for offline analysis',
      'Clone existing scenarios to create variations quickly',
    ],
    warnings: [
      'Scenarios are planning tools only - they don\'t affect actual budgets',
      'Shared scenarios can be viewed by everyone with access',
    ],
    tryItAction: {
      label: 'Create a Scenario',
      path: '/budget/scenarios/new',
      description: 'Build a what-if budget scenario',
    },
  },

  'budget-vs-actual': {
    instructions: [
      { step: 1, text: 'Navigate to Reports → Budget vs Actual', highlight: 'nav-bva' },
      { step: 2, text: 'Select the fiscal year and period range', highlight: 'period-selector' },
      { step: 3, text: 'Choose departments or cost centers to include', highlight: 'dept-filter' },
      { step: 4, text: 'View the variance analysis showing over/under budget items', highlight: 'variance-table' },
      { step: 5, text: 'Click any line item to drill down to transaction details', highlight: 'drill-down' },
      { step: 6, text: 'Export the report or schedule automatic delivery', highlight: 'export-btn' },
    ],
    tips: [
      'Use the "Favorites" button to save frequently-used report configurations',
      'Set up email alerts for variances exceeding your threshold',
      'The trend chart shows spending patterns over time',
    ],
    tryItAction: {
      label: 'View Report',
      path: '/reports/budget-vs-actual',
      description: 'Open the Budget vs Actual report',
    },
  },

  'department-overview': {
    instructions: [
      { step: 1, text: 'Click "Departments" in the main navigation', highlight: 'nav-departments' },
      { step: 2, text: 'Use the org chart or list view to find your department', highlight: 'view-toggle' },
      { step: 3, text: 'Click a department to see its budget summary', highlight: 'dept-card' },
      { step: 4, text: 'Review budget allocation by category (personnel, operations, etc.)', highlight: 'category-breakdown' },
      { step: 5, text: 'Check the spending trend chart for YTD progress', highlight: 'trend-chart' },
      { step: 6, text: 'View subordinate units by clicking "Show Sub-units"', highlight: 'sub-units-btn' },
    ],
    tips: [
      'Bookmark departments you manage for quick access',
      'Use the compare feature to benchmark against similar departments',
      'The "At Risk" indicator shows departments approaching budget limits',
    ],
    tryItAction: {
      label: 'Explore Departments',
      path: '/departments',
      description: 'Browse the department hierarchy',
    },
  },

  'budget-model': {
    instructions: [
      { step: 1, text: 'Access Budget → Model from the main menu (admin required)', highlight: 'nav-model' },
      { step: 2, text: 'Review the current allocation methodology overview', highlight: 'model-overview' },
      { step: 3, text: 'Click "Edit Drivers" to modify allocation factors', highlight: 'edit-drivers-btn' },
      { step: 4, text: 'Adjust weights for each cost driver (headcount, space, etc.)', highlight: 'driver-weights' },
      { step: 5, text: 'Preview the impact of changes before applying', highlight: 'preview-btn' },
      { step: 6, text: 'Save changes and schedule when to apply (immediate or future date)', highlight: 'apply-btn' },
    ],
    tips: [
      'Use the simulation mode to test changes without affecting live data',
      'Document your rationale for model changes in the notes field',
      'Export the allocation matrix for audit documentation',
    ],
    warnings: [
      'Model changes affect all department allocations - review carefully',
      'Changes require appropriate admin permissions',
    ],
    tryItAction: {
      label: 'View Budget Model',
      path: '/budget/model',
      description: 'Explore the allocation methodology',
    },
  },

  // Campus Management
  'campus-summary': {
    instructions: [
      { step: 1, text: 'Navigate to Campus Summary from the main menu', highlight: 'nav-campus' },
      { step: 2, text: 'View the institution-wide financial health dashboard', highlight: 'health-dashboard' },
      { step: 3, text: 'Check the fund balance indicators for each major fund', highlight: 'fund-balances' },
      { step: 4, text: 'Review cross-department comparisons in the benchmark section', highlight: 'benchmarks' },
      { step: 5, text: 'Click any metric to drill down to department details', highlight: 'metric-drill' },
      { step: 6, text: 'Use the time period selector to view trends', highlight: 'period-selector' },
    ],
    tips: [
      'The traffic light indicators show quick status: green (on track), yellow (watch), red (action needed)',
      'Export summary reports for board presentations',
      'Set up dashboard email digests for leadership',
    ],
    tryItAction: {
      label: 'View Campus Summary',
      path: '/campus',
      description: 'See the institution-wide financial overview',
    },
  },

  // Research Features
  'research-portfolio': {
    instructions: [
      { step: 1, text: 'Go to Research → Portfolio from the main menu', highlight: 'nav-research' },
      { step: 2, text: 'View your active grants and their current status', highlight: 'grants-list' },
      { step: 3, text: 'Check the burn rate chart showing spending vs. timeline', highlight: 'burn-rate' },
      { step: 4, text: 'Click a grant to see detailed financials', highlight: 'grant-detail' },
      { step: 5, text: 'Review upcoming milestones and deliverables', highlight: 'milestones' },
      { step: 6, text: 'Use filters to focus on specific sponsors or date ranges', highlight: 'filters' },
    ],
    tips: [
      'Set up alerts for grants approaching end dates or budget thresholds',
      'Use the "At Risk" filter to identify grants needing attention',
      'Export portfolio data for sponsor reports',
    ],
    tryItAction: {
      label: 'View Portfolio',
      path: '/research/portfolio',
      description: 'Explore your research grant portfolio',
    },
  },

  'research-explorer': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Explorer', highlight: 'nav-explorer' },
      { step: 2, text: 'Use the search bar to find grants by PI, title, or sponsor', highlight: 'search-bar' },
      { step: 3, text: 'Apply filters for award type, status, or department', highlight: 'filters' },
      { step: 4, text: 'View results in card, table, or chart view', highlight: 'view-toggle' },
      { step: 5, text: 'Click any grant for detailed information', highlight: 'grant-card' },
      { step: 6, text: 'Use the analytics tab for aggregate insights', highlight: 'analytics-tab' },
    ],
    tips: [
      'Save searches for quick access to common queries',
      'Use advanced search for complex queries (AND/OR logic)',
      'Export results to Excel or CSV for further analysis',
    ],
    tryItAction: {
      label: 'Search Grants',
      path: '/research/explorer',
      description: 'Search and analyze research grants',
    },
  },

  'research-proposals': {
    instructions: [
      { step: 1, text: 'Go to Research → Proposals to view the proposal pipeline', highlight: 'nav-proposals' },
      { step: 2, text: 'See proposals organized by stage (draft, submitted, awarded)', highlight: 'pipeline-view' },
      { step: 3, text: 'Click "New Proposal" to start a new submission', highlight: 'new-proposal-btn' },
      { step: 4, text: 'Fill in sponsor, program, and budget details', highlight: 'proposal-form' },
      { step: 5, text: 'Route for internal review and approval', highlight: 'review-routing' },
      { step: 6, text: 'Track submission status and sponsor feedback', highlight: 'status-tracker' },
    ],
    tips: [
      'Use templates for common sponsors to save time',
      'Set up calendar reminders for upcoming deadlines',
      'Track success rates by sponsor to inform future proposals',
    ],
    tryItAction: {
      label: 'View Proposals',
      path: '/research/proposals',
      description: 'Manage your proposal pipeline',
    },
  },

  'research-budget-builder': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Budget Builder', highlight: 'nav-budget-builder' },
      { step: 2, text: 'Select a sponsor template (NIH, NSF, DOE, etc.)', highlight: 'sponsor-template' },
      { step: 3, text: 'Enter personnel with effort percentages', highlight: 'personnel-section' },
      { step: 4, text: 'System auto-calculates fringe benefits at current rates', highlight: 'fringe-calc' },
      { step: 5, text: 'Add equipment, supplies, travel, and other costs', highlight: 'other-costs' },
      { step: 6, text: 'F&A (indirect) costs calculate automatically', highlight: 'fa-calc' },
      { step: 7, text: 'Generate sponsor-formatted budget and justification', highlight: 'generate-btn' },
    ],
    tips: [
      'Use the "Copy from Previous" feature to duplicate budgets',
      'Check the compliance panel for sponsor-specific rules',
      'Save drafts frequently - auto-save is every 2 minutes',
    ],
    warnings: [
      'Verify F&A rates with your sponsored programs office',
      'Some sponsors have specific budget format requirements',
    ],
    tryItAction: {
      label: 'Build a Budget',
      path: '/research/budget-builder',
      description: 'Create a compliant research budget',
    },
  },

  'research-subawards': {
    instructions: [
      { step: 1, text: 'Go to Research → Subawards', highlight: 'nav-subawards' },
      { step: 2, text: 'View active subaward agreements and their status', highlight: 'subaward-list' },
      { step: 3, text: 'Click "New Subaward" to create an agreement', highlight: 'new-subaward-btn' },
      { step: 4, text: 'Enter subrecipient details and budget', highlight: 'subrecipient-form' },
      { step: 5, text: 'Track invoices and payments against the agreement', highlight: 'invoice-tracker' },
      { step: 6, text: 'Monitor subrecipient spending with the burn rate chart', highlight: 'burn-rate' },
    ],
    tips: [
      'Set up invoice reminders for subrecipients',
      'Use the risk assessment tool for new subrecipients',
      'Track modifications to subaward agreements over time',
    ],
    tryItAction: {
      label: 'View Subawards',
      path: '/research/subawards',
      description: 'Manage subaward agreements',
    },
  },

  'research-closeout': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Closeout Readiness', highlight: 'nav-closeout' },
      { step: 2, text: 'View grants ending within the next 90 days', highlight: 'ending-grants' },
      { step: 3, text: 'Check the closeout checklist for each grant', highlight: 'checklist' },
      { step: 4, text: 'Review outstanding obligations and encumbrances', highlight: 'obligations' },
      { step: 5, text: 'Verify all required reports are submitted', highlight: 'reports' },
      { step: 6, text: 'Complete final financial reconciliation', highlight: 'reconciliation' },
    ],
    tips: [
      'Start closeout preparation 90 days before grant end',
      'Use the timeline view to see all upcoming closeouts',
      'Set up automatic reminders for PI and department admin',
    ],
    warnings: [
      'Late closeout can affect future funding eligibility',
      'Final invoices may be due 60-90 days after grant end',
    ],
    tryItAction: {
      label: 'View Closeout Status',
      path: '/research/closeout',
      description: 'Check closeout readiness for ending grants',
    },
  },

  'research-compliance': {
    instructions: [
      { step: 1, text: 'Go to Research → Compliance & Risk', highlight: 'nav-compliance' },
      { step: 2, text: 'View the compliance dashboard with risk indicators', highlight: 'risk-dashboard' },
      { step: 3, text: 'Review flagged items requiring attention', highlight: 'flagged-items' },
      { step: 4, text: 'Check effort certification status', highlight: 'effort-cert' },
      { step: 5, text: 'Monitor cost sharing commitments', highlight: 'cost-sharing' },
      { step: 6, text: 'Generate compliance reports for audits', highlight: 'audit-reports' },
    ],
    tips: [
      'Address high-risk items within 5 business days',
      'Schedule quarterly compliance reviews',
      'Document all compliance actions for audit trail',
    ],
    warnings: [
      'Unresolved compliance issues can jeopardize future funding',
      'Effort certification deadlines are strictly enforced',
    ],
    tryItAction: {
      label: 'View Compliance',
      path: '/research/compliance',
      description: 'Monitor research compliance status',
    },
  },

  // Capital Planning
  'capital-projects': {
    instructions: [
      { step: 1, text: 'Navigate to Capital → Projects from the main menu', highlight: 'nav-capital' },
      { step: 2, text: 'View the project list with status indicators', highlight: 'project-list' },
      { step: 3, text: 'Click a project to see detailed timeline and budget', highlight: 'project-detail' },
      { step: 4, text: 'Review the Gantt chart showing milestones', highlight: 'gantt-chart' },
      { step: 5, text: 'Check funding sources and draw schedules', highlight: 'funding-sources' },
      { step: 6, text: 'Monitor change orders and their budget impact', highlight: 'change-orders' },
    ],
    tips: [
      'Use the map view to see project locations across campus',
      'Filter by project phase, type, or funding source',
      'Export project reports for steering committee meetings',
    ],
    tryItAction: {
      label: 'View Projects',
      path: '/capital/projects',
      description: 'Explore capital projects',
    },
  },

  'capital-planning': {
    instructions: [
      { step: 1, text: 'Go to Capital → Planning for multi-year planning', highlight: 'nav-planning' },
      { step: 2, text: 'View the 5-year capital plan overview', highlight: 'five-year-view' },
      { step: 3, text: 'Add new projects to the planning pipeline', highlight: 'add-project-btn' },
      { step: 4, text: 'Prioritize projects using the scoring matrix', highlight: 'scoring-matrix' },
      { step: 5, text: 'Allocate funding from available sources', highlight: 'funding-allocation' },
      { step: 6, text: 'Generate the capital budget request', highlight: 'generate-request' },
    ],
    tips: [
      'Use the what-if tool to model different funding scenarios',
      'Link projects to strategic plan objectives',
      'Review facility condition assessments to prioritize',
    ],
    tryItAction: {
      label: 'Open Planning',
      path: '/capital/planning',
      description: 'Plan multi-year capital investments',
    },
  },

  'capital-portfolio': {
    instructions: [
      { step: 1, text: 'Navigate to Capital → Portfolio Dashboard', highlight: 'nav-portfolio' },
      { step: 2, text: 'View the portfolio summary with total investment', highlight: 'portfolio-summary' },
      { step: 3, text: 'Check project status distribution chart', highlight: 'status-chart' },
      { step: 4, text: 'Review budget vs. actual across all projects', highlight: 'budget-actual' },
      { step: 5, text: 'Identify projects at risk using the heatmap', highlight: 'risk-heatmap' },
      { step: 6, text: 'Drill down to individual project details', highlight: 'drill-down' },
    ],
    tips: [
      'Set up portfolio-level alerts for budget or schedule variances',
      'Use the trend analysis to identify systemic issues',
      'Export portfolio metrics for board reporting',
    ],
    tryItAction: {
      label: 'View Portfolio',
      path: '/capital/portfolio',
      description: 'See your capital project portfolio',
    },
  },

  // Tuition & Enrollment
  'fee-projection': {
    instructions: [
      { step: 1, text: 'Navigate to Tuition → Fee Projections', highlight: 'nav-fee' },
      { step: 2, text: 'Select the academic year to model', highlight: 'year-selector' },
      { step: 3, text: 'Enter proposed tuition rate changes', highlight: 'rate-input' },
      { step: 4, text: 'Input enrollment assumptions by student type', highlight: 'enrollment-input' },
      { step: 5, text: 'View projected revenue by category', highlight: 'revenue-projection' },
      { step: 6, text: 'Compare scenarios side-by-side', highlight: 'scenario-compare' },
      { step: 7, text: 'Generate board presentation materials', highlight: 'generate-report' },
    ],
    tips: [
      'Model best-case and worst-case enrollment scenarios',
      'Include fee waivers and discounts in your calculations',
      'Link projections to your financial aid strategy',
    ],
    tryItAction: {
      label: 'Create Projection',
      path: '/tuition/projections',
      description: 'Model tuition and fee scenarios',
    },
  },

  'enrollment-funnel': {
    instructions: [
      { step: 1, text: 'Go to Enrollment → Funnel Analysis', highlight: 'nav-funnel' },
      { step: 2, text: 'View the admissions pipeline from inquiry to enrolled', highlight: 'funnel-chart' },
      { step: 3, text: 'Check conversion rates at each stage', highlight: 'conversion-rates' },
      { step: 4, text: 'Compare to historical trends and benchmarks', highlight: 'trend-compare' },
      { step: 5, text: 'Filter by student type, major, or term', highlight: 'filters' },
      { step: 6, text: 'Project final enrollment based on current pipeline', highlight: 'projection' },
    ],
    tips: [
      'Track yield rates by geographic region or high school',
      'Set up alerts for conversion rate drops',
      'Use the cohort comparison to spot trends',
    ],
    tryItAction: {
      label: 'View Funnel',
      path: '/enrollment/funnel',
      description: 'Analyze your enrollment pipeline',
    },
  },

  // Analytics & Reporting
  'analytics-bi': {
    instructions: [
      { step: 1, text: 'Navigate to Analytics from the main menu', highlight: 'nav-analytics' },
      { step: 2, text: 'Browse pre-built dashboards by category', highlight: 'dashboard-gallery' },
      { step: 3, text: 'Click a dashboard to view and interact with it', highlight: 'dashboard-view' },
      { step: 4, text: 'Use filters to customize the view', highlight: 'dashboard-filters' },
      { step: 5, text: 'Create custom reports using the report builder', highlight: 'report-builder' },
      { step: 6, text: 'Schedule reports for automatic delivery', highlight: 'schedule-btn' },
    ],
    tips: [
      'Save custom filters as personal or shared views',
      'Export charts as images for presentations',
      'Use SQL mode for advanced custom queries',
    ],
    tryItAction: {
      label: 'Open Analytics',
      path: '/analytics',
      description: 'Explore dashboards and reports',
    },
  },

  'budget-book': {
    instructions: [
      { step: 1, text: 'Go to Reports → Budget Book', highlight: 'nav-budget-book' },
      { step: 2, text: 'Select the fiscal year for the budget book', highlight: 'year-selector' },
      { step: 3, text: 'Choose which sections to include', highlight: 'section-selector' },
      { step: 4, text: 'Customize the cover page and branding', highlight: 'branding' },
      { step: 5, text: 'Preview the document before generating', highlight: 'preview-btn' },
      { step: 6, text: 'Generate PDF or Word document', highlight: 'generate-btn' },
    ],
    tips: [
      'Use templates from previous years for consistency',
      'Include executive summary and highlights section',
      'Generate in advance and review with leadership',
    ],
    tryItAction: {
      label: 'Create Budget Book',
      path: '/reports/budget-book',
      description: 'Generate formal budget documentation',
    },
  },

  'deficit-centers': {
    instructions: [
      { step: 1, text: 'Navigate to Reports → Deficit Cost Centers', highlight: 'nav-deficit' },
      { step: 2, text: 'View the list of cost centers with deficit balances', highlight: 'deficit-list' },
      { step: 3, text: 'Sort by deficit amount or percentage', highlight: 'sort-options' },
      { step: 4, text: 'Click a cost center to see detailed analysis', highlight: 'detail-view' },
      { step: 5, text: 'Review the deficit recovery plan if applicable', highlight: 'recovery-plan' },
      { step: 6, text: 'Export for follow-up with department managers', highlight: 'export-btn' },
    ],
    tips: [
      'Set up alerts for cost centers approaching deficit',
      'Track deficit trends over multiple periods',
      'Link recovery plans to budget transfers',
    ],
    warnings: [
      'Persistent deficits may trigger institutional review',
      'Contact Finance for deficit carryforward policies',
    ],
    tryItAction: {
      label: 'View Deficits',
      path: '/reports/deficit-centers',
      description: 'Identify cost centers with deficits',
    },
  },

  // Workflow & Approvals
  'approval-queue': {
    instructions: [
      { step: 1, text: 'Click on Approvals in the main menu', highlight: 'nav-approvals' },
      { step: 2, text: 'View your pending approval requests', highlight: 'pending-list' },
      { step: 3, text: 'Click a request to review details', highlight: 'request-detail' },
      { step: 4, text: 'Review supporting documents and comments', highlight: 'documents' },
      { step: 5, text: 'Approve, reject, or request more information', highlight: 'action-buttons' },
      { step: 6, text: 'Add comments to explain your decision', highlight: 'comments-field' },
    ],
    tips: [
      'Use bulk approve for similar routine requests',
      'Set up a delegate when you\'re out of office',
      'Check the history tab to see past decisions',
    ],
    warnings: [
      'Approval deadlines are shown - overdue items are highlighted',
      'Some approvals may have dollar threshold limits',
    ],
    tryItAction: {
      label: 'View Approvals',
      path: '/approvals',
      description: 'Process pending approval requests',
    },
  },

  'workflow-builder': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Workflows (admin access required)', highlight: 'nav-workflows' },
      { step: 2, text: 'Click "Create Workflow" to design a new approval flow', highlight: 'create-workflow-btn' },
      { step: 3, text: 'Drag steps from the palette to the canvas', highlight: 'workflow-palette' },
      { step: 4, text: 'Connect steps to define the flow sequence', highlight: 'connections' },
      { step: 5, text: 'Configure conditions and approver assignments', highlight: 'step-config' },
      { step: 6, text: 'Set up notifications for each step', highlight: 'notifications' },
      { step: 7, text: 'Test the workflow before activating', highlight: 'test-btn' },
    ],
    tips: [
      'Use parallel branches for simultaneous approvals',
      'Add escalation rules for overdue approvals',
      'Clone existing workflows as starting points',
    ],
    warnings: [
      'Test thoroughly before activating new workflows',
      'Consider the impact on existing requests when modifying',
    ],
    tryItAction: {
      label: 'Build Workflow',
      path: '/admin/workflows',
      description: 'Design custom approval workflows',
    },
  },

  // HR / Personnel
  positions: {
    instructions: [
      { step: 1, text: 'Navigate to HR → Position Budgeting', highlight: 'nav-positions' },
      { step: 2, text: 'View positions organized by department', highlight: 'position-list' },
      { step: 3, text: 'Click a position to see funding details', highlight: 'position-detail' },
      { step: 4, text: 'Initiate a funding transfer between cost centers', highlight: 'transfer-btn' },
      { step: 5, text: 'Review the position history and changes', highlight: 'history-tab' },
      { step: 6, text: 'Generate position reports for planning', highlight: 'reports-btn' },
    ],
    tips: [
      'Use the vacancy report to identify open positions',
      'Track FTE allocations across funding sources',
      'Set up alerts for positions approaching budget limits',
    ],
    tryItAction: {
      label: 'View Positions',
      path: '/hr/positions',
      description: 'Manage position budgeting',
    },
  },

  'personnel-whatif': {
    instructions: [
      { step: 1, text: 'Go to HR → Personnel What-If Analysis', highlight: 'nav-whatif' },
      { step: 2, text: 'Create a new scenario for staffing changes', highlight: 'new-scenario-btn' },
      { step: 3, text: 'Add, modify, or remove positions in the scenario', highlight: 'position-changes' },
      { step: 4, text: 'Adjust salary and benefit assumptions', highlight: 'salary-adjust' },
      { step: 5, text: 'View the financial impact summary', highlight: 'impact-summary' },
      { step: 6, text: 'Compare multiple staffing scenarios', highlight: 'compare-btn' },
    ],
    tips: [
      'Model reorganization impacts before implementation',
      'Include benefit cost changes in your scenarios',
      'Share scenarios with leadership for planning discussions',
    ],
    tryItAction: {
      label: 'Create What-If',
      path: '/hr/whatif',
      description: 'Model personnel changes',
    },
  },

  // Admin & Settings
  admin: {
    instructions: [
      { step: 1, text: 'Access the Admin Panel from the main menu', highlight: 'nav-admin' },
      { step: 2, text: 'Navigate between sections using the sidebar', highlight: 'admin-sidebar' },
      { step: 3, text: 'Manage users and role assignments', highlight: 'users-section' },
      { step: 4, text: 'Configure system settings and defaults', highlight: 'settings-section' },
      { step: 5, text: 'Review audit logs for security monitoring', highlight: 'audit-logs' },
      { step: 6, text: 'Manage integrations and data connections', highlight: 'integrations' },
    ],
    tips: [
      'Use role templates for consistent permission assignment',
      'Schedule regular user access reviews',
      'Export audit logs for compliance documentation',
    ],
    warnings: [
      'Admin changes take effect immediately',
      'Review impact before modifying system settings',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Access system administration',
    },
  },

  'help-center': {
    instructions: [
      { step: 1, text: 'Click the Help icon or navigate to Help Center', highlight: 'nav-help' },
      { step: 2, text: 'Browse articles by category or search', highlight: 'help-search' },
      { step: 3, text: 'Watch video tutorials for visual learning', highlight: 'video-section' },
      { step: 4, text: 'Access the FAQ for common questions', highlight: 'faq-section' },
      { step: 5, text: 'Submit a support ticket if needed', highlight: 'support-ticket' },
      { step: 6, text: 'Check system status and announcements', highlight: 'status-section' },
    ],
    tips: [
      'Use Cmd/Ctrl + ? as a shortcut to open help',
      'Bookmark frequently used articles',
      'Check release notes for new features',
    ],
    tryItAction: {
      label: 'Open Help',
      path: '/help',
      description: 'Access documentation and support',
    },
  },

  'account-settings': {
    instructions: [
      { step: 1, text: 'Click your profile icon and select Settings', highlight: 'profile-menu' },
      { step: 2, text: 'Update your profile information', highlight: 'profile-section' },
      { step: 3, text: 'Configure notification preferences', highlight: 'notifications-section' },
      { step: 4, text: 'Set your default dashboard and views', highlight: 'defaults-section' },
      { step: 5, text: 'Toggle dark mode and accessibility options', highlight: 'appearance' },
      { step: 6, text: 'Manage connected devices and sessions', highlight: 'security-section' },
    ],
    tips: [
      'Enable two-factor authentication for security',
      'Set notification quiet hours to avoid after-hours alerts',
      'Choose your preferred email digest frequency',
    ],
    tryItAction: {
      label: 'Open Settings',
      path: '/settings',
      description: 'Customize your account preferences',
    },
  },

  // Research Commitments
  'research-commitments': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Commitments from the main menu', highlight: 'nav-commitments' },
      { step: 2, text: 'View your active cost-sharing and matching commitments', highlight: 'commitments-list' },
      { step: 3, text: 'Check commitment status and fulfillment progress', highlight: 'status-indicators' },
      { step: 4, text: 'Click a commitment to see detailed breakdown', highlight: 'commitment-detail' },
      { step: 5, text: 'Track actual contributions against committed amounts', highlight: 'contributions' },
      { step: 6, text: 'Generate compliance reports for sponsors', highlight: 'reports-btn' },
    ],
    tips: [
      'Set up alerts for commitments approaching due dates',
      'Link commitments to specific fund sources for tracking',
      'Use the dashboard view to see all commitments at a glance',
    ],
    warnings: [
      'Unfulfilled commitments can jeopardize future funding',
      'Document all cost-sharing contributions for audit purposes',
    ],
    tryItAction: {
      label: 'View Commitments',
      path: '/research/commitments',
      description: 'Manage research cost-sharing obligations',
    },
  },

  // Revenue Recognition
  'revenue-recognition': {
    instructions: [
      { step: 1, text: 'Navigate to Finance → Revenue Recognition', highlight: 'nav-revenue' },
      { step: 2, text: 'View deferred revenue schedules by category', highlight: 'schedule-list' },
      { step: 3, text: 'Check recognition timing and upcoming releases', highlight: 'timing-chart' },
      { step: 4, text: 'Click a schedule to see detailed recognition plan', highlight: 'schedule-detail' },
      { step: 5, text: 'Adjust recognition periods if needed', highlight: 'adjust-btn' },
      { step: 6, text: 'Generate journal entries for period close', highlight: 'journal-entries' },
    ],
    tips: [
      'Review recognition schedules before each period close',
      'Use the forecast view to project future revenue',
      'Set up automatic journal entry generation',
    ],
    warnings: [
      'Changes to recognition schedules may require audit approval',
      'Ensure compliance with ASC 606 revenue recognition standards',
    ],
    tryItAction: {
      label: 'View Revenue',
      path: '/finance/revenue-recognition',
      description: 'Manage deferred revenue schedules',
    },
  },

  // Program Margins
  'program-margins': {
    instructions: [
      { step: 1, text: 'Navigate to Finance → Program Margins', highlight: 'nav-margins' },
      { step: 2, text: 'View margin analysis by program or department', highlight: 'margin-table' },
      { step: 3, text: 'Check direct vs. indirect cost allocation', highlight: 'cost-breakdown' },
      { step: 4, text: 'Compare margins across time periods', highlight: 'trend-analysis' },
      { step: 5, text: 'Drill down to individual cost components', highlight: 'drill-down' },
      { step: 6, text: 'Export analysis for strategic planning', highlight: 'export-btn' },
    ],
    tips: [
      'Use the benchmark feature to compare against similar programs',
      'Set margin targets and track progress over time',
      'Review outliers to identify optimization opportunities',
    ],
    tryItAction: {
      label: 'View Margins',
      path: '/finance/program-margins',
      description: 'Analyze program profitability',
    },
  },

  // Forms & Submissions
  'form-builder': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Form Builder', highlight: 'nav-forms' },
      { step: 2, text: 'Click "Create Form" to start a new form', highlight: 'create-form-btn' },
      { step: 3, text: 'Drag and drop field types from the palette', highlight: 'field-palette' },
      { step: 4, text: 'Configure field properties (required, validation, etc.)', highlight: 'field-config' },
      { step: 5, text: 'Set up form sections and conditional logic', highlight: 'logic-builder' },
      { step: 6, text: 'Configure submission workflow and notifications', highlight: 'workflow-config' },
      { step: 7, text: 'Preview and publish the form', highlight: 'publish-btn' },
    ],
    tips: [
      'Use templates to start from common form layouts',
      'Test forms thoroughly before publishing',
      'Clone existing forms to create variations quickly',
    ],
    warnings: [
      'Published forms cannot have major structure changes',
      'Consider data retention policies for form submissions',
    ],
    prerequisites: [
      'Form admin or system admin role',
      'Understanding of your data collection requirements',
    ],
    tryItAction: {
      label: 'Create Form',
      path: '/admin/forms/new',
      description: 'Build a new dynamic form',
    },
  },

  'form-submissions': {
    instructions: [
      { step: 1, text: 'Navigate to Forms → My Submissions or All Submissions', highlight: 'nav-submissions' },
      { step: 2, text: 'Filter submissions by form type, status, or date', highlight: 'filters' },
      { step: 3, text: 'Click a submission to view details', highlight: 'submission-row' },
      { step: 4, text: 'Review submitted data and attachments', highlight: 'submission-data' },
      { step: 5, text: 'Process the submission (approve, reject, request changes)', highlight: 'action-buttons' },
      { step: 6, text: 'Export submissions for reporting', highlight: 'export-btn' },
    ],
    tips: [
      'Use saved filters for frequently accessed submission types',
      'Set up email notifications for new submissions',
      'Bulk process similar submissions to save time',
    ],
    tryItAction: {
      label: 'View Submissions',
      path: '/forms/submissions',
      description: 'Review and process form submissions',
    },
  },

  'public-forms': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Forms and find your form', highlight: 'forms-list' },
      { step: 2, text: 'Click the "Public Access" settings', highlight: 'public-settings' },
      { step: 3, text: 'Enable public access and configure permissions', highlight: 'access-toggle' },
      { step: 4, text: 'Customize the public form URL', highlight: 'url-config' },
      { step: 5, text: 'Set up captcha and rate limiting', highlight: 'security-settings' },
      { step: 6, text: 'Copy the public link to share', highlight: 'copy-link' },
    ],
    tips: [
      'Use custom branding for external-facing forms',
      'Set up confirmation emails for submitters',
      'Monitor submission rates to detect spam',
    ],
    warnings: [
      'Public forms are accessible without login',
      'Ensure no sensitive data is exposed in form fields',
    ],
    tryItAction: {
      label: 'Configure Public Forms',
      path: '/admin/forms',
      description: 'Set up external form access',
    },
  },

  // ========================================
  // ADDITIONAL WALKTHROUGHS
  // ========================================

  // Keyboard Shortcuts
  'keyboard-shortcuts': {
    instructions: [
      { step: 1, text: 'Press ? (question mark) anywhere to see available shortcuts', highlight: 'shortcut-help' },
      { step: 2, text: 'Use Cmd+K (Mac) or Ctrl+K (Windows) to open global search', highlight: 'search-shortcut' },
      { step: 3, text: 'Press D to quickly navigate to Dashboard', highlight: 'dashboard-shortcut' },
      { step: 4, text: 'Use Q to toggle the Quick Actions menu', highlight: 'quick-actions-shortcut' },
      { step: 5, text: 'Press Escape to close modals and dialogs', highlight: 'escape-shortcut' },
      { step: 6, text: 'Arrow keys navigate through lists and search results', highlight: 'arrow-keys' },
    ],
    tips: [
      'Shortcuts are disabled when typing in text fields',
      'Most Mac shortcuts use Cmd, Windows/Linux use Ctrl',
      'Learn 3-5 core shortcuts first, then add more as needed',
      'Print the shortcut reference for quick access',
    ],
    tryItAction: {
      label: 'View Shortcuts',
      action: 'showShortcuts',
      description: 'Press ? to open the shortcuts reference',
    },
  },

  // Transfer Approvals
  'transfers-approve': {
    instructions: [
      { step: 1, text: 'Navigate to Transfers from the main menu', highlight: 'nav-transfers' },
      { step: 2, text: 'Filter the list to show "Pending" status transfers', highlight: 'status-filter' },
      { step: 3, text: 'Click on a transfer to view its details', highlight: 'transfer-row' },
      { step: 4, text: 'Review source, destination, amount, and justification', highlight: 'transfer-details' },
      { step: 5, text: 'Check the audit trail for prior approvals', highlight: 'audit-trail' },
      { step: 6, text: 'Click "Approve" or "Reject" with an optional comment', highlight: 'action-buttons' },
    ],
    tips: [
      'Use bulk approve for multiple routine transfers',
      'Set up a delegate when you are out of office',
      'Check budget availability before approving large transfers',
      'Approval history is fully auditable for compliance',
    ],
    warnings: [
      'Rejections should include feedback for the requester',
      'Once approved, transfers cannot be easily reversed',
    ],
    tryItAction: {
      label: 'View Pending Transfers',
      path: '/transfers?status=pending',
      description: 'Open transfers requiring your approval',
    },
  },

  // Research NOA Upload
  'research-noa': {
    instructions: [
      { step: 1, text: 'Navigate to Research from the main menu', highlight: 'nav-research' },
      { step: 2, text: 'Click "Upload NOA" button', highlight: 'upload-noa-btn' },
      { step: 3, text: 'Drag and drop or browse to select your NOA document (PDF)', highlight: 'file-drop' },
      { step: 4, text: 'Wait for automatic extraction of key information', highlight: 'extraction-progress' },
      { step: 5, text: 'Review the extracted data for accuracy', highlight: 'extracted-data' },
      { step: 6, text: 'Make corrections if needed and confirm details', highlight: 'confirm-btn' },
      { step: 7, text: 'The grant is created with the extracted information', highlight: 'grant-created' },
    ],
    tips: [
      'Supported formats include PDF and scanned documents',
      'Complex NOAs may require manual review of extracted data',
      'Save original documents in the project record for reference',
      'Use bulk upload for multiple NOAs at once',
    ],
    warnings: [
      'Always verify extracted amounts against the original document',
      'OCR accuracy depends on document quality',
    ],
    tryItAction: {
      label: 'Upload NOA',
      path: '/research?action=upload',
      description: 'Upload a Notice of Award document',
    },
  },

  // Research Workload
  'research-workload': {
    instructions: [
      { step: 1, text: 'Open Research Explorer from the sidebar', highlight: 'nav-explorer' },
      { step: 2, text: 'Enable Assignment Mode using the toggle', highlight: 'assignment-toggle' },
      { step: 3, text: 'View workload scores next to each staff member', highlight: 'workload-scores' },
      { step: 4, text: 'Hover over a staff member to see complexity breakdown', highlight: 'complexity-tooltip' },
      { step: 5, text: 'Use color indicators: green (low), yellow (medium), red (high)', highlight: 'workload-colors' },
      { step: 6, text: 'Consider workload balance when making assignments', highlight: 'assignment-panel' },
    ],
    tips: [
      'Complexity scores factor in award amount, personnel count, and sponsor type',
      'Federal awards score higher due to compliance requirements',
      'Subawards add complexity for subrecipient monitoring',
      'Administrators can adjust complexity weights in Research Settings',
    ],
    warnings: [
      'Overloaded staff may impact grant management quality',
      'Review workload before assigning additional awards',
    ],
    tryItAction: {
      label: 'View Workload',
      path: '/research/explorer',
      description: 'Open the Research Explorer to view staff workload',
    },
  },

  // Course Section Planning
  'courses': {
    instructions: [
      { step: 1, text: 'Navigate to Courses from the main menu', highlight: 'nav-courses' },
      { step: 2, text: 'Select the term you want to plan', highlight: 'term-selector' },
      { step: 3, text: 'View existing sections or import from catalog', highlight: 'section-list' },
      { step: 4, text: 'Click "Add Section" to create a new section', highlight: 'add-section-btn' },
      { step: 5, text: 'Set capacity, instructor, and schedule details', highlight: 'section-form' },
      { step: 6, text: 'Submit sections for departmental approval', highlight: 'submit-btn' },
    ],
    tips: [
      'Use historical enrollment data to set realistic capacities',
      'Check room availability before finalizing schedules',
      'Link sections to instructor workload calculations',
      'Track enrollment against planned capacity in real-time',
    ],
    warnings: [
      'Section changes may require department approval',
      'Check for scheduling conflicts before submitting',
    ],
    tryItAction: {
      label: 'Open Courses',
      path: '/courses',
      description: 'Manage course section planning',
    },
  },

  // User Management
  'user-management': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Users', highlight: 'nav-users' },
      { step: 2, text: 'View all users or search for a specific user', highlight: 'user-search' },
      { step: 3, text: 'Click "Add User" to create a new account', highlight: 'add-user-btn' },
      { step: 4, text: 'Enter user details: name, email, department', highlight: 'user-form' },
      { step: 5, text: 'Assign roles and department access permissions', highlight: 'role-assignment' },
      { step: 6, text: 'Set user status (active, inactive, locked)', highlight: 'status-toggle' },
    ],
    tips: [
      'Use role templates for consistent permission assignment',
      'Bulk import users from CSV for large batches',
      'Deactivate rather than delete accounts to preserve audit history',
      'Set up automatic provisioning via SCIM if available',
    ],
    warnings: [
      'Role changes take effect immediately on the user session',
      'Deactivated users cannot log in but their data is preserved',
    ],
    tryItAction: {
      label: 'Manage Users',
      path: '/admin/users',
      description: 'Open user management',
    },
  },

  // Navigation Restrictions (Role Page Access Control)
  'navigation-restrictions': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Roles', highlight: 'nav-roles' },
      { step: 2, text: 'Click Edit on the role you want to restrict, or create a new role', highlight: 'role-edit-btn' },
      { step: 3, text: 'Scroll down to the "Navigation Restrictions" section', highlight: 'nav-restrictions-section' },
      { step: 4, text: 'Toggle ON "Restrict navigation to specific pages"', highlight: 'restrict-toggle' },
      { step: 5, text: 'Select which pages this role can access by checking the boxes', highlight: 'page-checkboxes' },
      { step: 6, text: 'Use "Select All" or "Deselect All" buttons per category', highlight: 'bulk-buttons' },
      { step: 7, text: 'Changes save automatically - assign the role to users', highlight: 'assign-role' },
    ],
    tips: [
      'This feature works with ANY role - not just specific examples',
      'Combine with department scoping for even more targeted access',
      'Admin and superadmin roles cannot have navigation restrictions',
      'Users always retain access to Help, Account Settings, and login/logout',
      'If a parent route is selected, child routes are also accessible',
      'Test restrictions by logging in as a test user with that role',
    ],
    warnings: [
      'Ensure users have at least one page selected before enabling restrictions',
      'Users who try to access blocked pages will see a friendly redirect',
      'Navigation restrictions affect menu visibility AND direct URL access',
    ],
    tryItAction: {
      label: 'Manage Roles',
      path: '/admin#/roles',
      description: 'Open role management to configure navigation restrictions',
    },
  },

  // PI Role Example (Creating a restricted research role)
  'pi-role-setup': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Roles and click "Create"', highlight: 'create-role-btn' },
      { step: 2, text: 'Enter name: "Principal Investigator"', highlight: 'role-name-input' },
      { step: 3, text: 'Enable "Restrict to Assigned Departments"', highlight: 'dept-scope-toggle' },
      { step: 4, text: 'Click Save to create the role', highlight: 'save-btn' },
      { step: 5, text: 'In Permissions, expand "Research" and grant only view permissions', highlight: 'research-permissions' },
      { step: 6, text: 'Scroll to Navigation Restrictions and enable the toggle', highlight: 'nav-toggle' },
      { step: 7, text: 'Under Research category, select ONLY "Research Portfolio"', highlight: 'research-portfolio-checkbox' },
      { step: 8, text: 'Go to Admin → Users and assign this role to your PI users', highlight: 'user-role-assign' },
      { step: 9, text: 'Also assign each PI to their specific department', highlight: 'user-dept-assign' },
    ],
    tips: [
      'PIs will only see Research Portfolio in their navigation',
      'They can only view data from their assigned department',
      'Add more pages later if PIs need additional access',
      'This same pattern works for any restricted role (Finance, HR, etc.)',
    ],
    warnings: [
      'Make sure to select at least one page in Navigation Restrictions',
      'Test with a test account before rolling out to real users',
    ],
    tryItAction: {
      label: 'Create PI Role',
      path: '/admin#/roles/create',
      description: 'Create a new Principal Investigator role',
    },
  },

  // Workflow Setup
  'workflow-setup': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Workflows', highlight: 'nav-workflows' },
      { step: 2, text: 'View existing workflows or create a new one', highlight: 'workflow-list' },
      { step: 3, text: 'Define workflow triggers (e.g., transfer over $10,000)', highlight: 'trigger-config' },
      { step: 4, text: 'Add approval steps and assign approvers', highlight: 'steps-config' },
      { step: 5, text: 'Configure routing logic (parallel, sequential, conditional)', highlight: 'routing-config' },
      { step: 6, text: 'Test the workflow before activating', highlight: 'test-btn' },
    ],
    tips: [
      'Document workflow logic for auditors and new admins',
      'Set up escalation rules for overdue approvals',
      'Use delegation features for approver absences',
      'Monitor workflow performance metrics regularly',
    ],
    warnings: [
      'Test thoroughly before activating new workflows',
      'Changes may affect existing pending requests',
    ],
    tryItAction: {
      label: 'Configure Workflows',
      path: '/admin/workflows',
      description: 'Set up approval workflows',
    },
  },

  // ERP ETL Console
  'erp-etl': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → ERP ETL', highlight: 'nav-erp' },
      { step: 2, text: 'Review connection status and latest accounting date', highlight: 'connection-status' },
      { step: 3, text: 'Use "Pull GL" with a date window and limit', highlight: 'pull-gl-btn' },
      { step: 4, text: 'Toggle Simulate to test with mock data', highlight: 'simulate-toggle' },
      { step: 5, text: 'Use "Push Budgets" with scenario and fiscal year', highlight: 'push-budgets-btn' },
      { step: 6, text: 'Review results and refresh the status panel', highlight: 'results-panel' },
    ],
    tips: [
      'Start with a small date window to validate mappings',
      'Use dry run or simulate before enabling real pushes',
      'Confirm chartfields and departments are in sync with ERP codes',
      'Use the scheduler for automated nightly pulls',
    ],
    warnings: [
      'Large date ranges may take significant time to process',
      'Always verify results after initial setup',
    ],
    tryItAction: {
      label: 'Open ERP ETL',
      path: '/admin/erp',
      description: 'Access the ERP integration console',
    },
  },

  // Data Import
  'data-import': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Data Import', highlight: 'nav-data-import' },
      { step: 2, text: 'Choose the dataset type (Enrollment, COA, Fee Rates)', highlight: 'dataset-selector' },
      { step: 3, text: 'Select a CSV file and click "Validate"', highlight: 'file-upload' },
      { step: 4, text: 'Review preview rows and validation errors', highlight: 'validation-results' },
      { step: 5, text: 'Fix any errors in your file and re-validate', highlight: 'error-list' },
      { step: 6, text: 'Click "Upload" to start the import', highlight: 'upload-btn' },
      { step: 7, text: 'Monitor progress and review summary counts', highlight: 'progress-bar' },
    ],
    tips: [
      'Keep column headers consistent with export templates',
      'Use smaller files when testing to confirm formatting',
      'Imports run in the background; wait for completion',
      'Check the data import documentation for field requirements',
    ],
    warnings: [
      'Incorrect data formats may cause import failures',
      'Large imports may take several minutes',
    ],
    tryItAction: {
      label: 'Open Data Import',
      path: '/admin/data-import',
      description: 'Upload enrollment, COA, or fee data',
    },
  },

  // Validation Rules
  'validation-rules': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Validation Rules', highlight: 'nav-validation' },
      { step: 2, text: 'Create or select a rule set for a scope', highlight: 'ruleset-selector' },
      { step: 3, text: 'Add rules with conditions and requirements', highlight: 'add-rule-btn' },
      { step: 4, text: 'Configure if field, match value, and required/prohibited fields', highlight: 'rule-config' },
      { step: 5, text: 'Use the simulator to test sample payloads', highlight: 'simulator' },
      { step: 6, text: 'Publish the rule set to make it active', highlight: 'publish-btn' },
    ],
    tips: [
      'Use wildcards like 5* or ranges like 100-200 in match values',
      'Set severity to warn for soft policy checks',
      'Keep rules small and focused for easier troubleshooting',
      'Document rule intent in the error message',
    ],
    warnings: [
      'Published rules apply immediately to all submissions',
      'Test thoroughly before publishing to production',
    ],
    tryItAction: {
      label: 'Configure Rules',
      path: '/admin/validation-rules',
      description: 'Set up cross-field validation rules',
    },
  },

  // Form Submission Workflows
  'form-submission-workflows': {
    instructions: [
      { step: 1, text: 'Create a form in Admin → Form Builder', highlight: 'form-builder' },
      { step: 2, text: 'Assign a workflow to the form in settings', highlight: 'workflow-assignment' },
      { step: 3, text: 'Submit a test request from the form page', highlight: 'test-submit' },
      { step: 4, text: 'Review pending submissions in Admin → Form Submissions', highlight: 'submissions-list' },
      { step: 5, text: 'Approve or reject submissions as they progress', highlight: 'approval-actions' },
      { step: 6, text: 'View step history on the workflow status page', highlight: 'workflow-history' },
    ],
    tips: [
      'Keep workflows short to reduce approval bottlenecks',
      'Use clear step names for audit trails',
      'Share submission links with approvers who need context',
      'Pair with validation rules for policy enforcement',
    ],
    warnings: [
      'Workflow changes do not affect in-flight submissions',
      'Ensure all approvers have appropriate access',
    ],
    tryItAction: {
      label: 'Open Form Builder',
      path: '/admin/form-builder',
      description: 'Create forms with approval workflows',
    },
  },

  // Batch Operations
  'batch-operations': {
    instructions: [
      { step: 1, text: 'Navigate to the page with items to process in batch', highlight: 'item-list' },
      { step: 2, text: 'Use checkboxes to select multiple items', highlight: 'checkboxes' },
      { step: 3, text: 'Click the batch action button in the toolbar', highlight: 'batch-action-btn' },
      { step: 4, text: 'Choose the action to apply (approve, reject, export, etc.)', highlight: 'action-menu' },
      { step: 5, text: 'Confirm the batch operation', highlight: 'confirm-dialog' },
      { step: 6, text: 'Review the results summary', highlight: 'results-summary' },
    ],
    tips: [
      'Use "Select All" to select all items on the current page',
      'Batch operations respect your role permissions',
      'Failed items are reported individually for follow-up',
      'Consider using filters to narrow down items before batch actions',
    ],
    warnings: [
      'Batch operations cannot be easily undone',
      'Large batches may take time to process',
    ],
    tryItAction: {
      label: 'Try Batch Operations',
      path: '/transfers',
      description: 'Select multiple transfers to process in batch',
    },
  },

  // Export & Import Formats
  'export-import-formats': {
    instructions: [
      { step: 1, text: 'Navigate to any page with exportable data', highlight: 'data-page' },
      { step: 2, text: 'Click the export button (download icon)', highlight: 'export-btn' },
      { step: 3, text: 'Choose format: Excel (.xlsx), CSV, or PDF', highlight: 'format-selector' },
      { step: 4, text: 'For imports, use the matching template format', highlight: 'import-template' },
      { step: 5, text: 'Large exports may be delivered via email', highlight: 'email-delivery' },
    ],
    tips: [
      'Excel format preserves formatting and multiple sheets',
      'CSV is best for data processing and integrations',
      'PDF is ideal for sharing and printing',
      'Download templates before importing to ensure correct columns',
    ],
    warnings: [
      'Very large exports may be truncated or require scheduling',
      'Import files must match the expected column structure',
    ],
  },

  // Accessibility Features
  'accessibility': {
    instructions: [
      { step: 1, text: 'Use Tab to navigate between interactive elements', highlight: 'tab-nav' },
      { step: 2, text: 'Press Enter or Space to activate buttons and links', highlight: 'activation' },
      { step: 3, text: 'Use arrow keys to navigate within menus and dropdowns', highlight: 'arrow-nav' },
      { step: 4, text: 'Screen readers announce all interactive elements', highlight: 'screen-reader' },
      { step: 5, text: 'Enable high contrast mode in Account Settings', highlight: 'high-contrast' },
      { step: 6, text: 'Adjust text size using browser zoom (Cmd/Ctrl + or -)', highlight: 'text-size' },
    ],
    tips: [
      'Skip links at the top of each page jump to main content',
      'All images have descriptive alt text',
      'Focus indicators show which element is selected',
      'Color is never the only way information is conveyed',
    ],
    tryItAction: {
      label: 'Open Settings',
      path: '/settings',
      description: 'Configure accessibility preferences',
    },
  },

  // Research Analytics
  'research-analytics': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Analytics from the sidebar', highlight: 'nav-analytics' },
      { step: 2, text: 'View summary cards showing proposals, win rate, and funding totals', highlight: 'summary-cards' },
      { step: 3, text: 'Use the group-by selector to analyze by department, PI, or sponsor', highlight: 'group-selector' },
      { step: 4, text: 'Review the win rate chart to identify top-performing areas', highlight: 'win-rate-chart' },
      { step: 5, text: 'Analyze funding trends over time using the trend chart', highlight: 'trend-chart' },
      { step: 6, text: 'Export analytics data for further reporting', highlight: 'export-btn' },
    ],
    tips: [
      'Compare win rates across sponsors to identify strategic opportunities',
      'Use department grouping to benchmark performance',
      'Track trends over multiple fiscal years for pattern recognition',
      'Share analytics with leadership using the export feature',
    ],
    warnings: [
      'Win rates are calculated only for decided proposals',
      'Historical data accuracy depends on proposal tracking completeness',
    ],
    tryItAction: {
      label: 'View Analytics',
      path: '/research/analytics',
      description: 'Open the research analytics dashboard',
    },
  },

  // Research Financial Health
  'research-financial-health': {
    instructions: [
      { step: 1, text: 'Go to Research → Financial Health', highlight: 'nav-financial-health' },
      { step: 2, text: 'Review the burn rate summary showing spending status', highlight: 'burn-summary' },
      { step: 3, text: 'Check the burn rate distribution chart for portfolio overview', highlight: 'burn-distribution' },
      { step: 4, text: 'Identify underspending or overspending awards using color indicators', highlight: 'status-indicators' },
      { step: 5, text: 'View individual award details for spending patterns', highlight: 'award-details' },
      { step: 6, text: 'Set up alerts for awards approaching budget thresholds', highlight: 'alert-settings' },
    ],
    tips: [
      'Green indicates on-track spending relative to project timeline',
      'Yellow flags underspending which may indicate delayed activities',
      'Red indicates overspending requiring immediate attention',
      'Review financial health monthly to catch issues early',
    ],
    warnings: [
      'Overspending may require cost transfers or additional funding',
      'Underspending near award end may result in lost funding',
    ],
    tryItAction: {
      label: 'Check Financial Health',
      path: '/research/financial-health',
      description: 'Monitor research portfolio financial status',
    },
  },

  // Research Portfolio Performance
  'research-portfolio-performance': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Portfolio Performance', highlight: 'nav-performance' },
      { step: 2, text: 'View summary metrics: total awards, funding, and activity counts', highlight: 'summary-metrics' },
      { step: 3, text: 'Use group-by to analyze performance by sponsor, PI, or department', highlight: 'group-selector' },
      { step: 4, text: 'Review the performance chart showing top performers', highlight: 'performance-chart' },
      { step: 5, text: 'Analyze award duration and average amounts by category', highlight: 'duration-analysis' },
      { step: 6, text: 'Export performance data for stakeholder reporting', highlight: 'export-btn' },
    ],
    tips: [
      'Track performance trends to identify growing or declining areas',
      'Compare sponsor mix to understand funding diversification',
      'Use PI analysis for faculty development discussions',
      'Share performance dashboards with department chairs',
    ],
    warnings: [
      'Performance metrics reflect recorded awards only',
      'New PIs may show lower totals due to limited history',
    ],
    tryItAction: {
      label: 'View Performance',
      path: '/research/portfolio-performance',
      description: 'Analyze research portfolio performance metrics',
    },
  },

  // Research Personnel Effort
  'research-personnel-effort': {
    instructions: [
      { step: 1, text: 'Go to Research → Personnel Effort', highlight: 'nav-personnel-effort' },
      { step: 2, text: 'View effort summary showing total personnel and effort allocation', highlight: 'effort-summary' },
      { step: 3, text: 'Review effort distribution by role (PI, Co-PI, Staff, etc.)', highlight: 'role-distribution' },
      { step: 4, text: 'Check for over-committed individuals exceeding 100% effort', highlight: 'overcommit-alerts' },
      { step: 5, text: 'View the effort table for detailed personnel assignments', highlight: 'effort-table' },
      { step: 6, text: 'Filter by department or sponsor to focus analysis', highlight: 'filter-options' },
    ],
    tips: [
      'Effort certification deadlines require accurate effort records',
      'Flag personnel approaching 100% commitment before new proposals',
      'Review effort at proposal submission to prevent over-commitment',
      'Coordinate with HR for cost-sharing effort documentation',
    ],
    warnings: [
      'Over-committed effort may cause compliance audit findings',
      'Effort changes require updated effort certifications',
    ],
    tryItAction: {
      label: 'Review Effort',
      path: '/research/personnel-effort',
      description: 'Monitor personnel effort allocations',
    },
  },

  // Research Detail (Individual Award)
  'research-detail': {
    instructions: [
      { step: 1, text: 'Click on an award from Research Portfolio or Explorer', highlight: 'award-link' },
      { step: 2, text: 'View award overview: sponsor, dates, status, and amounts', highlight: 'award-overview' },
      { step: 3, text: 'Review budget breakdown by category and period', highlight: 'budget-breakdown' },
      { step: 4, text: 'Check personnel assignments and effort allocations', highlight: 'personnel-section' },
      { step: 5, text: 'Configure link rules to connect transactions automatically', highlight: 'link-rules' },
      { step: 6, text: 'Use AI insights for spend recommendations', highlight: 'ai-insights' },
    ],
    tips: [
      'Set up link rules early to automate transaction categorization',
      'Monitor burn rate against project timeline',
      'Review personnel effort quarterly for certification accuracy',
      'Document any budget modifications in notes',
    ],
    warnings: [
      'Link rule changes affect future transaction matching only',
      'Budget modifications may require sponsor approval',
    ],
    tryItAction: {
      label: 'View Sample Award',
      path: '/research',
      description: 'Open the research portfolio to select an award',
    },
  },

  // Research Proposal Detail
  'research-proposal-detail': {
    instructions: [
      { step: 1, text: 'Click on a proposal from Research Proposals list', highlight: 'proposal-link' },
      { step: 2, text: 'View proposal details: title, sponsor, dates, and amounts', highlight: 'proposal-overview' },
      { step: 3, text: 'Track stage progress through the proposal lifecycle', highlight: 'stage-progress' },
      { step: 4, text: 'Update stage as proposal moves through review and submission', highlight: 'stage-update' },
      { step: 5, text: 'Review compliance requirements (IRB, IACUC, IBC)', highlight: 'compliance-section' },
      { step: 6, text: 'Add budget details and deadline information', highlight: 'budget-deadlines' },
    ],
    tips: [
      'Update stages promptly for accurate pipeline reporting',
      'Set internal deadlines before sponsor deadlines',
      'Document compliance requirements early in the process',
      'Link proposals to resulting awards when funded',
    ],
    warnings: [
      'Missing compliance approvals can delay award setup',
      'Deadline changes should be communicated to all team members',
    ],
    tryItAction: {
      label: 'View Proposals',
      path: '/research/proposals',
      description: 'Open the research proposals list',
    },
  },

  // Research Settings
  'research-settings': {
    instructions: [
      { step: 1, text: 'Navigate to Research → Settings (requires admin access)', highlight: 'nav-settings' },
      { step: 2, text: 'Configure F&A rate and base for indirect cost calculations', highlight: 'fa-settings' },
      { step: 3, text: 'Set up auto-approval roles for workflow automation', highlight: 'approval-roles' },
      { step: 4, text: 'Adjust complexity weights for workload scoring', highlight: 'complexity-weights' },
      { step: 5, text: 'Configure alert thresholds for budget warnings', highlight: 'alert-thresholds' },
      { step: 6, text: 'Manage Gantt chart templates for proposal planning', highlight: 'gantt-templates' },
    ],
    tips: [
      'Update F&A rates annually or when negotiated rates change',
      'Test workflow changes in a sandbox before production',
      'Document complexity weight rationale for consistency',
      'Review alert thresholds with finance team',
    ],
    warnings: [
      'F&A changes affect all new budget calculations',
      'Role changes impact approval workflows immediately',
    ],
    tryItAction: {
      label: 'Open Settings',
      path: '/research/settings',
      description: 'Configure research module settings',
    },
  },

  // Research Subaward Detail
  'research-subaward-detail': {
    instructions: [
      { step: 1, text: 'Click on a subaward from the Subawards list', highlight: 'subaward-link' },
      { step: 2, text: 'View subaward details: subrecipient, period, and amounts', highlight: 'subaward-overview' },
      { step: 3, text: 'Track modifications and amendments history', highlight: 'modifications' },
      { step: 4, text: 'Monitor invoicing and payment status', highlight: 'invoicing-status' },
      { step: 5, text: 'Review compliance requirements and risk assessment', highlight: 'compliance-risk' },
      { step: 6, text: 'Check closeout status and final reporting', highlight: 'closeout-status' },
    ],
    tips: [
      'Set up invoice reminders to ensure timely payments',
      'Monitor subrecipient performance against milestones',
      'Document all modifications for audit purposes',
      'Start closeout process 60 days before end date',
    ],
    warnings: [
      'Late invoices may delay prime award reporting',
      'Subrecipient compliance issues can affect prime award',
    ],
    tryItAction: {
      label: 'View Subawards',
      path: '/research/subawards',
      description: 'Open the subawards list',
    },
  },

  // ============================================
  // USER SUBMISSIONS & WORKFLOW
  // ============================================

  'my-submissions': {
    instructions: [
      { step: 1, text: 'Navigate to Forms → My Submissions from the sidebar', highlight: 'nav-my-submissions' },
      { step: 2, text: 'View all forms you have submitted with their current status', highlight: 'submissions-list' },
      { step: 3, text: 'Use status filter tabs to show Draft, Pending, Approved, or Rejected submissions', highlight: 'status-tabs' },
      { step: 4, text: 'Search submissions by title or form name using the search box', highlight: 'search-box' },
      { step: 5, text: 'Click on a submission to view details or continue editing drafts', highlight: 'submission-row' },
      { step: 6, text: 'Use the Withdraw button to cancel pending submissions if needed', highlight: 'withdraw-btn' },
    ],
    tips: [
      'Draft submissions can be edited and submitted when ready',
      'Withdrawn submissions can be resubmitted after modifications',
      'Check the status badge to quickly see where your submission is in the workflow',
      'Use filters to focus on submissions needing your attention',
    ],
    warnings: [
      'Approved submissions cannot be modified',
      'Withdrawing a submission removes it from the approval queue',
    ],
    tryItAction: {
      label: 'View My Submissions',
      path: '/forms/my-submissions',
      description: 'Open your submissions list',
    },
  },

  'accessible-submissions': {
    instructions: [
      { step: 1, text: 'Navigate to Forms → Accessible Submissions from the sidebar', highlight: 'nav-accessible' },
      { step: 2, text: 'View submissions you have access to based on your role and permissions', highlight: 'submissions-grid' },
      { step: 3, text: 'Filter by status using the status tabs at the top', highlight: 'status-filter' },
      { step: 4, text: 'Search across all accessible submissions using the search field', highlight: 'search-field' },
      { step: 5, text: 'Click a submission to review details and take action if authorized', highlight: 'submission-card' },
      { step: 6, text: 'Use bulk actions to process multiple submissions at once', highlight: 'bulk-actions' },
    ],
    tips: [
      'Your access is determined by role assignments and department permissions',
      'Submissions from your department appear automatically',
      'Use this view to monitor form activity across your areas of responsibility',
    ],
    warnings: [
      'You can only take action on submissions where you have appropriate permissions',
    ],
    tryItAction: {
      label: 'View Accessible Submissions',
      path: '/forms/accessible-submissions',
      description: 'Open accessible submissions list',
    },
  },

  'workflow-status': {
    instructions: [
      { step: 1, text: 'Navigate to Workflow → Status from the sidebar', highlight: 'nav-workflow' },
      { step: 2, text: 'View all items currently in your approval workflows', highlight: 'workflow-list' },
      { step: 3, text: 'See the current step and approver for each item', highlight: 'current-step' },
      { step: 4, text: 'Click the "Why is this pending?" AI button for intelligent status explanation', highlight: 'ai-explain-btn' },
      { step: 5, text: 'Filter by workflow type, status, or date range', highlight: 'workflow-filters' },
      { step: 6, text: 'Track approval history and see all actions taken on an item', highlight: 'approval-history' },
    ],
    tips: [
      'The AI explanation feature analyzes workflow rules to explain current status',
      'Set up notifications to be alerted when items need your attention',
      'Use the timeline view to see approval progression',
      'Export workflow reports for audit purposes',
    ],
    warnings: [
      'Items stuck in workflow may indicate missing approvers or configuration issues',
    ],
    tryItAction: {
      label: 'View Workflow Status',
      path: '/workflow',
      description: 'Open workflow status page',
    },
  },

  // ============================================
  // DEPARTMENT & PERSONNEL DETAILS
  // ============================================

  'department-detail': {
    instructions: [
      { step: 1, text: 'Navigate to a department from Department Overview or search', highlight: 'nav-department' },
      { step: 2, text: 'View the department header with budget summary and variance indicators', highlight: 'dept-header' },
      { step: 3, text: 'Review the PCN (Position Control Number) table showing all positions', highlight: 'pcn-table' },
      { step: 4, text: 'Use the Routing section to see budget routing and fund flows', highlight: 'routing-section' },
      { step: 5, text: 'Click on individual positions to view detailed allocation information', highlight: 'position-link' },
      { step: 6, text: 'Access funding tables to see revenue sources and expenditure categories', highlight: 'funding-tables' },
      { step: 7, text: 'Use quick actions to initiate transfers or run reports', highlight: 'quick-actions' },
    ],
    tips: [
      'Variance percentages help identify budget areas needing attention',
      'The PCN table can be sorted and filtered by various attributes',
      'Click column headers to sort the position list',
      'Use the export button to download department data',
    ],
    warnings: [
      'Budget changes require appropriate approval workflows',
      'Position modifications may affect payroll and HR systems',
    ],
    tryItAction: {
      label: 'View Departments',
      path: '/departments',
      description: 'Open department overview',
    },
  },

  'personnel-detail': {
    instructions: [
      { step: 1, text: 'Click on a position from department view or search for personnel', highlight: 'nav-personnel' },
      { step: 2, text: 'View employee information header with name, title, and status', highlight: 'employee-header' },
      { step: 3, text: 'Review funding allocations showing how salary is distributed across sources', highlight: 'allocations-table' },
      { step: 4, text: 'View the What-If scenario section to model compensation changes', highlight: 'whatif-section' },
      { step: 5, text: 'Access transfer history to see past funding movements', highlight: 'transfer-history' },
      { step: 6, text: 'Use the Compensation Adjustment panel for salary modeling', highlight: 'comp-adjustment' },
      { step: 7, text: 'Review effort distribution across projects and cost centers', highlight: 'effort-distribution' },
    ],
    tips: [
      'Allocation percentages must total 100% across all funding sources',
      'What-If scenarios do not affect actual data until approved',
      'Historical data helps understand funding patterns over time',
      'Use notes to document reasons for allocation changes',
    ],
    warnings: [
      'Allocation changes affect budget projections immediately in reports',
      'Compensation adjustments require HR approval workflows',
    ],
    tryItAction: {
      label: 'View Personnel',
      path: '/positions',
      description: 'Open position budgeting',
    },
  },

  // ============================================
  // CAPITAL PROJECT DETAILS
  // ============================================

  'capital-project-detail': {
    instructions: [
      { step: 1, text: 'Navigate to Capital → Projects and click on a project', highlight: 'nav-capital' },
      { step: 2, text: 'View project header with status, budget, and timeline summary', highlight: 'project-header' },
      { step: 3, text: 'Use the Overview tab for high-level project information and KPIs', highlight: 'overview-tab' },
      { step: 4, text: 'Review the Timeline tab for milestones and schedule tracking', highlight: 'timeline-tab' },
      { step: 5, text: 'Access the Budget tab for detailed cost breakdown and forecasting', highlight: 'budget-tab' },
      { step: 6, text: 'Check the Funding tab to see funding sources and draw schedules', highlight: 'funding-tab' },
      { step: 7, text: 'Use the Documents tab to manage project files and attachments', highlight: 'documents-tab' },
    ],
    tips: [
      'The status indicator shows project health at a glance',
      'Budget variance alerts highlight cost overruns early',
      'Milestone tracking helps identify schedule risks',
      'Document management keeps all project files organized',
    ],
    warnings: [
      'Budget modifications may require board approval',
      'Timeline changes should be reflected in official project plans',
    ],
    tryItAction: {
      label: 'View Capital Projects',
      path: '/capital/projects',
      description: 'Open capital projects list',
    },
  },

  'capital-change-orders': {
    instructions: [
      { step: 1, text: 'Open a capital project and navigate to the Change Orders tab', highlight: 'change-orders-tab' },
      { step: 2, text: 'View list of all change orders with status and amounts', highlight: 'co-list' },
      { step: 3, text: 'Click Add Change Order to create a new change request', highlight: 'add-co-btn' },
      { step: 4, text: 'Enter change order details including scope, cost impact, and justification', highlight: 'co-form' },
      { step: 5, text: 'Submit for approval through the configured workflow', highlight: 'submit-co' },
      { step: 6, text: 'Track approval status and view approval history', highlight: 'co-status' },
    ],
    tips: [
      'Document justification thoroughly for audit purposes',
      'Include impact on timeline when applicable',
      'Attach supporting documentation for approvers',
      'Change orders above threshold require additional approvals',
    ],
    warnings: [
      'Approved change orders permanently modify project budget',
      'Large change orders may require board notification',
    ],
    tryItAction: {
      label: 'View Capital Projects',
      path: '/capital/projects',
      description: 'Open capital projects to manage change orders',
    },
  },

  'capital-phases': {
    instructions: [
      { step: 1, text: 'Open a capital project and navigate to the Phases tab', highlight: 'phases-tab' },
      { step: 2, text: 'View project phases with budget allocations and timelines', highlight: 'phases-list' },
      { step: 3, text: 'Click on a phase to view detailed breakdown', highlight: 'phase-detail' },
      { step: 4, text: 'Track phase completion percentage and actual vs planned spend', highlight: 'phase-progress' },
      { step: 5, text: 'Update phase status as work progresses', highlight: 'update-phase' },
      { step: 6, text: 'Review phase-level funding sources and draws', highlight: 'phase-funding' },
    ],
    tips: [
      'Phases help organize large projects into manageable segments',
      'Each phase can have its own budget and timeline',
      'Phase reporting provides granular project visibility',
      'Use phase milestones for progress tracking',
    ],
    warnings: [
      'Phase budget changes affect overall project totals',
    ],
    tryItAction: {
      label: 'View Capital Projects',
      path: '/capital/projects',
      description: 'Open capital projects to manage phases',
    },
  },

  'capital-contractors': {
    instructions: [
      { step: 1, text: 'Open a capital project and navigate to the Contractors tab', highlight: 'contractors-tab' },
      { step: 2, text: 'View all contractors assigned to the project', highlight: 'contractor-list' },
      { step: 3, text: 'Add new contractors using the Add Contractor button', highlight: 'add-contractor' },
      { step: 4, text: 'Review contract amounts, payment terms, and status', highlight: 'contract-details' },
      { step: 5, text: 'Track payments and retainage for each contractor', highlight: 'payments-tracking' },
      { step: 6, text: 'Manage contractor documents and certifications', highlight: 'contractor-docs' },
    ],
    tips: [
      'Keep contractor insurance certificates up to date',
      'Track retainage to ensure proper release timing',
      'Document all contract modifications',
      'Use payment history for cash flow planning',
    ],
    warnings: [
      'Contractor payments require proper approval workflows',
      'Insurance lapse may pause payment processing',
    ],
    tryItAction: {
      label: 'View Capital Projects',
      path: '/capital/projects',
      description: 'Open capital projects to manage contractors',
    },
  },

  'capital-space-impact': {
    instructions: [
      { step: 1, text: 'Open a capital project and navigate to the Space Impact tab', highlight: 'space-tab' },
      { step: 2, text: 'View current and projected space metrics', highlight: 'space-metrics' },
      { step: 3, text: 'Review assignable square footage changes', highlight: 'asf-changes' },
      { step: 4, text: 'See impact on department space allocations', highlight: 'dept-allocations' },
      { step: 5, text: 'Model different space scenarios', highlight: 'space-scenarios' },
      { step: 6, text: 'Link space changes to facilities management', highlight: 'facilities-link' },
    ],
    tips: [
      'Space metrics affect indirect cost calculations',
      'Document space classification for compliance',
      'Consider multi-use space designations',
      'Coordinate with facilities for accurate data',
    ],
    warnings: [
      'Space changes may affect F&A rate calculations',
    ],
    tryItAction: {
      label: 'View Capital Projects',
      path: '/capital/projects',
      description: 'Open capital projects to view space impact',
    },
  },

  // ============================================
  // ADMIN DATA MANAGEMENT
  // ============================================

  'admin-tree-management': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Organization Trees from the sidebar', highlight: 'nav-admin-trees' },
      { step: 2, text: 'Select the tree type to manage (Department, Budget, Reporting)', highlight: 'tree-selector' },
      { step: 3, text: 'View the hierarchical structure with expand/collapse controls', highlight: 'tree-view' },
      { step: 4, text: 'Drag and drop nodes to reorganize the hierarchy', highlight: 'drag-drop' },
      { step: 5, text: 'Right-click on a node for actions like add, edit, or delete', highlight: 'context-menu' },
      { step: 6, text: 'Use the effective date picker for scheduled changes', highlight: 'effective-date' },
    ],
    tips: [
      'Changes can be scheduled for future effective dates',
      'Preview impacts before saving reorganizations',
      'Export tree structure for documentation',
      'Use search to quickly find nodes in large trees',
    ],
    warnings: [
      'Tree changes affect reporting and data aggregation',
      'Deleted nodes may impact historical reports',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for tree management',
    },
  },

  'admin-budget-drivers': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Budget Drivers', highlight: 'nav-drivers' },
      { step: 2, text: 'View list of configured budget drivers (enrollment, credit hours, etc.)', highlight: 'drivers-list' },
      { step: 3, text: 'Click Add Driver to create a new budget driver', highlight: 'add-driver' },
      { step: 4, text: 'Configure driver formula and data sources', highlight: 'driver-config' },
      { step: 5, text: 'Set driver weights and allocation rules', highlight: 'driver-weights' },
      { step: 6, text: 'Test driver calculations with sample data', highlight: 'test-driver' },
    ],
    tips: [
      'Drivers automate budget allocations based on metrics',
      'Multiple drivers can be combined with weights',
      'Historical driver data supports trend analysis',
      'Document driver logic for audit trails',
    ],
    warnings: [
      'Driver changes affect all linked budget calculations',
      'Test thoroughly before activating new drivers',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for budget drivers',
    },
  },

  'admin-notifications': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Notification Settings', highlight: 'nav-notifications' },
      { step: 2, text: 'View notification templates organized by event type', highlight: 'templates-list' },
      { step: 3, text: 'Edit templates to customize message content', highlight: 'edit-template' },
      { step: 4, text: 'Configure delivery channels (email, in-app, SMS)', highlight: 'channels-config' },
      { step: 5, text: 'Set up notification rules and triggers', highlight: 'trigger-rules' },
      { step: 6, text: 'Test notifications with preview functionality', highlight: 'preview-test' },
    ],
    tips: [
      'Use merge fields to personalize notifications',
      'Set appropriate frequency to avoid notification fatigue',
      'Different channels suit different urgency levels',
      'Review delivery reports for troubleshooting',
    ],
    warnings: [
      'Disabled notifications may cause missed deadlines',
      'Test templates before deploying changes',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for notifications',
    },
  },

  'admin-report-scheduling': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Scheduled Reports', highlight: 'nav-scheduling' },
      { step: 2, text: 'View list of scheduled reports with run times', highlight: 'schedule-list' },
      { step: 3, text: 'Click Add Schedule to set up a new recurring report', highlight: 'add-schedule' },
      { step: 4, text: 'Select the report template and parameters', highlight: 'report-params' },
      { step: 5, text: 'Configure schedule frequency and distribution list', highlight: 'schedule-config' },
      { step: 6, text: 'Set output format and delivery method', highlight: 'output-settings' },
    ],
    tips: [
      'Schedule reports during off-peak hours for performance',
      'Use distribution lists for consistent delivery',
      'Archive reports for compliance requirements',
      'Monitor failed deliveries in the log',
    ],
    warnings: [
      'Large reports may take time to generate',
      'Check recipient permissions for sensitive reports',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for report scheduling',
    },
  },

  'admin-bulk-transfers': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Bulk Operations → Transfers', highlight: 'nav-bulk' },
      { step: 2, text: 'Download the bulk transfer template', highlight: 'download-template' },
      { step: 3, text: 'Fill in transfer details following the template format', highlight: 'template-format' },
      { step: 4, text: 'Upload the completed file using the import button', highlight: 'upload-file' },
      { step: 5, text: 'Review validation results and fix any errors', highlight: 'validation-review' },
      { step: 6, text: 'Submit the batch for processing and approval', highlight: 'submit-batch' },
    ],
    tips: [
      'Validate data before upload to reduce errors',
      'Use the template exactly as provided',
      'Large batches may be processed in chunks',
      'Keep original files for audit purposes',
    ],
    warnings: [
      'Bulk transfers still require approval workflows',
      'Errors in batch may reject entire upload',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for bulk operations',
    },
  },

  'admin-security': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Security Settings', highlight: 'nav-security' },
      { step: 2, text: 'Review user roles and permission assignments', highlight: 'roles-list' },
      { step: 3, text: 'Configure role-based access control (RBAC) rules', highlight: 'rbac-config' },
      { step: 4, text: 'Set up row-level security for data isolation', highlight: 'row-security' },
      { step: 5, text: 'Review audit logs for security monitoring', highlight: 'audit-logs' },
      { step: 6, text: 'Configure SSO and authentication settings', highlight: 'sso-config' },
    ],
    tips: [
      'Use role inheritance to simplify permission management',
      'Regularly review user access for compliance',
      'Enable audit logging for sensitive operations',
      'Test permissions changes with a test user',
    ],
    warnings: [
      'Permission changes take effect immediately',
      'Incorrect security settings may expose sensitive data',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for security settings',
    },
  },

  'admin-user-management': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → User Management', highlight: 'nav-users' },
      { step: 2, text: 'View the list of all users with status and role information', highlight: 'user-list' },
      { step: 3, text: 'Click Add User to invite a new user', highlight: 'add-user' },
      { step: 4, text: 'Assign roles and department access to the user', highlight: 'role-assignment' },
      { step: 5, text: 'Configure user preferences and notification settings', highlight: 'user-prefs' },
      { step: 6, text: 'Use bulk import for adding multiple users', highlight: 'bulk-import' },
    ],
    tips: [
      'Use SSO integration for seamless authentication',
      'Assign minimal necessary permissions',
      'Deactivate rather than delete former users for audit trails',
      'Set up approval workflows for new user requests',
    ],
    warnings: [
      'Deactivated users cannot log in immediately',
      'Role changes affect user access in real-time',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for user management',
    },
  },

  'data-import-export': {
    instructions: [
      { step: 1, text: 'Navigate to Admin → Data Import/Export', highlight: 'nav-import-export' },
      { step: 2, text: 'Select the data type to import or export', highlight: 'data-type-select' },
      { step: 3, text: 'For imports, download and fill out the template', highlight: 'download-template' },
      { step: 4, text: 'Upload files and review validation results', highlight: 'upload-validate' },
      { step: 5, text: 'For exports, configure filters and output format', highlight: 'export-config' },
      { step: 6, text: 'Schedule recurring imports/exports if needed', highlight: 'schedule-recurring' },
    ],
    tips: [
      'Use CSV format for best compatibility',
      'Validate data before large imports',
      'Archive exports for compliance',
      'Set up automated integrations for regular data flows',
    ],
    warnings: [
      'Large imports may take significant time',
      'Exported data may contain sensitive information',
    ],
    tryItAction: {
      label: 'Open Admin',
      path: '/admin',
      description: 'Open admin panel for data operations',
    },
  },
};

/**
 * Get how-to steps for a specific step ID
 * @param {string} stepId - The step ID
 * @returns {Object|null} How-to configuration or null if not found
 */
export function getHowToSteps(stepId) {
  return HOW_TO_STEPS[stepId] || null;
}

export default {
  STEP_MEDIA,
  ACHIEVEMENTS,
  USER_GOALS,
  HELP_ARTICLES,
  HOW_TO_STEPS,
  getEstimatedTime,
  formatTime,
  getHowToSteps,
};
